/*!
 * ScrollTrigger 3.12.5
 * https://gsap.com
 *
 * @license Copyright 2008-2024, GreenSock. All rights reserved.
 * Subject to the terms at https://gsap.com/standard-license or for
 * Club GSAP members, the agreement issued with that membership.
 * @author: Jack Doyle, jack@greensock.com
 */

/* eslint-disable */
import {
    Observer,
    _getTarget,
    _vertical,
    _horizontal,
    _scrollers,
    _proxies,
    _getScrollFunc,
    _getProxyProp,
    _getVelocityProp
} from './Observer.js';

var gsap,
    _coreInitted,
    _win,
    _doc,
    _docEl,
    _body,
    _root,
    _resizeDelay,
    _toArray,
    _clamp,
    _time2,
    _syncInterval,
    _refreshing,
    _pointerIsDown,
    _transformProp,
    _i,
    _prevWidth,
    _prevHeight,
    _autoRefresh,
    _sort,
    _suppressOverwrites,
    _ignoreResize,
    _normalizer,
    _ignoreMobileResize,
    _baseScreenHeight,
    _baseScreenWidth,
    _fixIOSBug,
    _context,
    _scrollRestoration,
    _div100vh,
    _100vh,
    _isReverted,
    _clampingMax,
    _limitCallbacks,
    // if true, we'll only trigger callbacks if the active state toggles, so if you scroll immediately past both the start and end positions of a ScrollTrigger (thus inactive to inactive), neither its onEnter nor onLeave will be called. This is useful during startup.
    _startup = 1,
    _getTime = Date.now,
    _time1 = _getTime(),
    _lastScrollTime = 0,
    _enabled = 0,
    _parseClamp = function _parseClamp(value, type, self) {
        var clamp = _isString(value) && (value.substr(0, 6) === 'clamp(' || value.indexOf('max') > -1);
        self['_' + type + 'Clamp'] = clamp;
        return clamp ? value.substr(6, value.length - 7) : value;
    },
    _keepClamp = function _keepClamp(value, clamp) {
        return clamp && (!_isString(value) || value.substr(0, 6) !== 'clamp(') ? 'clamp(' + value + ')' : value;
    },
    _rafBugFix = function _rafBugFix() {
        return _enabled && requestAnimationFrame(_rafBugFix);
    },
    // in some browsers (like Firefox), screen repaints weren't consistent unless we had SOMETHING queued up in requestAnimationFrame()! So this just creates a super simple loop to keep it alive and smooth out repaints.
    _pointerDownHandler = function _pointerDownHandler() {
        return (_pointerIsDown = 1);
    },
    _pointerUpHandler = function _pointerUpHandler() {
        return (_pointerIsDown = 0);
    },
    _passThrough = function _passThrough(v) {
        return v;
    },
    _round = function _round(value) {
        return Math.round(value * 100000) / 100000 || 0;
    },
    _windowExists = function _windowExists() {
        return typeof window !== 'undefined';
    },
    _getGSAP = function _getGSAP() {
        return gsap || (_windowExists() && (gsap = window.gsap) && gsap.registerPlugin && gsap);
    },
    _isViewport = function _isViewport(e) {
        return !!~_root.indexOf(e);
    },
    _getViewportDimension = function _getViewportDimension(dimensionProperty) {
        return (
            (dimensionProperty === 'Height' ? _100vh : _win['inner' + dimensionProperty]) ||
            _docEl['client' + dimensionProperty] ||
            _body['client' + dimensionProperty]
        );
    },
    _getBoundsFunc = function _getBoundsFunc(element) {
        return (
            _getProxyProp(element, 'getBoundingClientRect') ||
            (_isViewport(element)
                ? function () {
                      _winOffsets.width = _win.innerWidth;
                      _winOffsets.height = _100vh;
                      return _winOffsets;
                  }
                : function () {
                      return _getBounds(element);
                  })
        );
    },
    _getSizeFunc = function _getSizeFunc(scroller, isViewport, _ref) {
        var d = _ref.d,
            d2 = _ref.d2,
            a = _ref.a;
        return (a = _getProxyProp(scroller, 'getBoundingClientRect'))
            ? function () {
                  return a()[d];
              }
            : function () {
                  return (isViewport ? _getViewportDimension(d2) : scroller['client' + d2]) || 0;
              };
    },
    _getOffsetsFunc = function _getOffsetsFunc(element, isViewport) {
        return !isViewport || ~_proxies.indexOf(element)
            ? _getBoundsFunc(element)
            : function () {
                  return _winOffsets;
              };
    },
    _maxScroll = function _maxScroll(element, _ref2) {
        var s = _ref2.s,
            d2 = _ref2.d2,
            d = _ref2.d,
            a = _ref2.a;
        return Math.max(
            0,
            (s = 'scroll' + d2) && (a = _getProxyProp(element, s))
                ? a() - _getBoundsFunc(element)()[d]
                : _isViewport(element)
                ? (_docEl[s] || _body[s]) - _getViewportDimension(d2)
                : element[s] - element['offset' + d2]
        );
    },
    _iterateAutoRefresh = function _iterateAutoRefresh(func, events) {
        for (var i = 0; i < _autoRefresh.length; i += 3) {
            (!events || ~events.indexOf(_autoRefresh[i + 1])) &&
                func(_autoRefresh[i], _autoRefresh[i + 1], _autoRefresh[i + 2]);
        }
    },
    _isString = function _isString(value) {
        return typeof value === 'string';
    },
    _isFunction = function _isFunction(value) {
        return typeof value === 'function';
    },
    _isNumber = function _isNumber(value) {
        return typeof value === 'number';
    },
    _isObject = function _isObject(value) {
        return typeof value === 'object';
    },
    _endAnimation = function _endAnimation(animation, reversed, pause) {
        return animation && animation.progress(reversed ? 0 : 1) && pause && animation.pause();
    },
    _callback = function _callback(self, func) {
        if (self.enabled) {
            var result = self._ctx
                ? self._ctx.add(function () {
                      return func(self);
                  })
                : func(self);
            result && result.totalTime && (self.callbackAnimation = result);
        }
    },
    _abs = Math.abs,
    _left = 'left',
    _top = 'top',
    _right = 'right',
    _bottom = 'bottom',
    _width = 'width',
    _height = 'height',
    _Right = 'Right',
    _Left = 'Left',
    _Top = 'Top',
    _Bottom = 'Bottom',
    _padding = 'padding',
    _margin = 'margin',
    _Width = 'Width',
    _Height = 'Height',
    _px = 'px',
    _getComputedStyle = function _getComputedStyle(element) {
        return _win.getComputedStyle(element);
    },
    _makePositionable = function _makePositionable(element) {
        // if the element already has position: absolute or fixed, leave that, otherwise make it position: relative
        var position = _getComputedStyle(element).position;

        element.style.position = position === 'absolute' || position === 'fixed' ? position : 'relative';
    },
    _setDefaults = function _setDefaults(obj, defaults) {
        for (var p in defaults) {
            p in obj || (obj[p] = defaults[p]);
        }

        return obj;
    },
    _getBounds = function _getBounds(element, withoutTransforms) {
        var tween =
                withoutTransforms &&
                _getComputedStyle(element)[_transformProp] !== 'matrix(1, 0, 0, 1, 0, 0)' &&
                gsap
                    .to(element, {
                        x: 0,
                        y: 0,
                        xPercent: 0,
                        yPercent: 0,
                        rotation: 0,
                        rotationX: 0,
                        rotationY: 0,
                        scale: 1,
                        skewX: 0,
                        skewY: 0
                    })
                    .progress(1),
            bounds = element.getBoundingClientRect();
        tween && tween.progress(0).kill();
        return bounds;
    },
    _getSize = function _getSize(element, _ref3) {
        var d2 = _ref3.d2;
        return element['offset' + d2] || element['client' + d2] || 0;
    },
    _getLabelRatioArray = function _getLabelRatioArray(timeline) {
        var a = [],
            labels = timeline.labels,
            duration = timeline.duration(),
            p;

        for (p in labels) {
            a.push(labels[p] / duration);
        }

        return a;
    },
    _getClosestLabel = function _getClosestLabel(animation) {
        return function (value) {
            return gsap.utils.snap(_getLabelRatioArray(animation), value);
        };
    },
    _snapDirectional = function _snapDirectional(snapIncrementOrArray) {
        var snap = gsap.utils.snap(snapIncrementOrArray),
            a =
                Array.isArray(snapIncrementOrArray) &&
                snapIncrementOrArray.slice(0).sort(function (a, b) {
                    return a - b;
                });
        return a
            ? function (value, direction, threshold) {
                  if (threshold === void 0) {
                      threshold = 1e-3;
                  }

                  var i;

                  if (!direction) {
                      return snap(value);
                  }

                  if (direction > 0) {
                      value -= threshold; // to avoid rounding errors. If we're too strict, it might snap forward, then immediately again, and again.

                      for (i = 0; i < a.length; i++) {
                          if (a[i] >= value) {
                              return a[i];
                          }
                      }

                      return a[i - 1];
                  } else {
                      i = a.length;
                      value += threshold;

                      while (i--) {
                          if (a[i] <= value) {
                              return a[i];
                          }
                      }
                  }

                  return a[0];
              }
            : function (value, direction, threshold) {
                  if (threshold === void 0) {
                      threshold = 1e-3;
                  }

                  var snapped = snap(value);
                  return !direction || Math.abs(snapped - value) < threshold || snapped - value < 0 === direction < 0
                      ? snapped
                      : snap(direction < 0 ? value - snapIncrementOrArray : value + snapIncrementOrArray);
              };
    },
    _getLabelAtDirection = function _getLabelAtDirection(timeline) {
        return function (value, st) {
            return _snapDirectional(_getLabelRatioArray(timeline))(value, st.direction);
        };
    },
    _multiListener = function _multiListener(func, element, types, callback) {
        return types.split(',').forEach(function (type) {
            return func(element, type, callback);
        });
    },
    _addListener = function _addListener(element, type, func, nonPassive, capture) {
        return element.addEventListener(type, func, {
            passive: !nonPassive,
            capture: !!capture
        });
    },
    _removeListener = function _removeListener(element, type, func, capture) {
        return element.removeEventListener(type, func, !!capture);
    },
    _wheelListener = function _wheelListener(func, el, scrollFunc) {
        scrollFunc = scrollFunc && scrollFunc.wheelHandler;

        if (scrollFunc) {
            func(el, 'wheel', scrollFunc);
            func(el, 'touchmove', scrollFunc);
        }
    },
    _markerDefaults = {
        startColor: 'green',
        endColor: 'red',
        indent: 0,
        fontSize: '16px',
        fontWeight: 'normal'
    },
    _defaults = {
        toggleActions: 'play',
        anticipatePin: 0
    },
    _keywords = {
        top: 0,
        left: 0,
        center: 0.5,
        bottom: 1,
        right: 1
    },
    _offsetToPx = function _offsetToPx(value, size) {
        if (_isString(value)) {
            var eqIndex = value.indexOf('='),
                relative = ~eqIndex ? +(value.charAt(eqIndex - 1) + 1) * parseFloat(value.substr(eqIndex + 1)) : 0;

            if (~eqIndex) {
                value.indexOf('%') > eqIndex && (relative *= size / 100);
                value = value.substr(0, eqIndex - 1);
            }

            value =
                relative +
                (value in _keywords
                    ? _keywords[value] * size
                    : ~value.indexOf('%')
                    ? (parseFloat(value) * size) / 100
                    : parseFloat(value) || 0);
        }

        return value;
    },
    _createMarker = function _createMarker(
        type,
        name,
        container,
        direction,
        _ref4,
        offset,
        matchWidthEl,
        containerAnimation
    ) {
        var startColor = _ref4.startColor,
            endColor = _ref4.endColor,
            fontSize = _ref4.fontSize,
            indent = _ref4.indent,
            fontWeight = _ref4.fontWeight;

        var e = _doc.createElement('div'),
            useFixedPosition = _isViewport(container) || _getProxyProp(container, 'pinType') === 'fixed',
            isScroller = type.indexOf('scroller') !== -1,
            parent = useFixedPosition ? _body : container,
            isStart = type.indexOf('start') !== -1,
            color = isStart ? startColor : endColor,
            css =
                'border-color:' +
                color +
                ';font-size:' +
                fontSize +
                ';color:' +
                color +
                ';font-weight:' +
                fontWeight +
                ';pointer-events:none;white-space:nowrap;font-family:sans-serif,Arial;z-index:1000;padding:4px 8px;border-width:0;border-style:solid;';

        css += 'position:' + ((isScroller || containerAnimation) && useFixedPosition ? 'fixed;' : 'absolute;');
        (isScroller || containerAnimation || !useFixedPosition) &&
            (css += (direction === _vertical ? _right : _bottom) + ':' + (offset + parseFloat(indent)) + 'px;');
        matchWidthEl && (css += 'box-sizing:border-box;text-align:left;width:' + matchWidthEl.offsetWidth + 'px;');
        e._isStart = isStart;
        e.setAttribute('class', 'gsap-marker-' + type + (name ? ' marker-' + name : ''));
        e.style.cssText = css;
        e.innerText = name || name === 0 ? type + '-' + name : type;
        parent.children[0] ? parent.insertBefore(e, parent.children[0]) : parent.appendChild(e);
        e._offset = e['offset' + direction.op.d2];

        _positionMarker(e, 0, direction, isStart);

        return e;
    },
    _positionMarker = function _positionMarker(marker, start, direction, flipped) {
        var vars = {
                display: 'block'
            },
            side = direction[flipped ? 'os2' : 'p2'],
            oppositeSide = direction[flipped ? 'p2' : 'os2'];
        marker._isFlipped = flipped;
        vars[direction.a + 'Percent'] = flipped ? -100 : 0;
        vars[direction.a] = flipped ? '1px' : 0;
        vars['border' + side + _Width] = 1;
        vars['border' + oppositeSide + _Width] = 0;
        vars[direction.p] = start + 'px';
        gsap.set(marker, vars);
    },
    _triggers = [],
    _ids = {},
    _rafID,
    _sync = function _sync() {
        return _getTime() - _lastScrollTime > 34 && (_rafID || (_rafID = requestAnimationFrame(_updateAll)));
    },
    _onScroll = function _onScroll() {
        // previously, we tried to optimize performance by batching/deferring to the next requestAnimationFrame(), but discovered that Safari has a few bugs that make this unworkable (especially on iOS). See https://codepen.io/GreenSock/pen/16c435b12ef09c38125204818e7b45fc?editors=0010 and https://codepen.io/GreenSock/pen/JjOxYpQ/3dd65ccec5a60f1d862c355d84d14562?editors=0010 and https://codepen.io/GreenSock/pen/ExbrPNa/087cef197dc35445a0951e8935c41503?editors=0010
        if (!_normalizer || !_normalizer.isPressed || _normalizer.startX > _body.clientWidth) {
            // if the user is dragging the scrollbar, allow it.
            _scrollers.cache++;

            if (_normalizer) {
                _rafID || (_rafID = requestAnimationFrame(_updateAll));
            } else {
                _updateAll(); // Safari in particular (on desktop) NEEDS the immediate update rather than waiting for a requestAnimationFrame() whereas iOS seems to benefit from waiting for the requestAnimationFrame() tick, at least when normalizing. See https://codepen.io/GreenSock/pen/qBYozqO?editors=0110
            }

            _lastScrollTime || _dispatch('scrollStart');
            _lastScrollTime = _getTime();
        }
    },
    _setBaseDimensions = function _setBaseDimensions() {
        _baseScreenWidth = _win.innerWidth;
        _baseScreenHeight = _win.innerHeight;
    },
    _onResize = function _onResize() {
        _scrollers.cache++;
        !_refreshing &&
            !_ignoreResize &&
            !_doc.fullscreenElement &&
            !_doc.webkitFullscreenElement &&
            (!_ignoreMobileResize ||
                _baseScreenWidth !== _win.innerWidth ||
                Math.abs(_win.innerHeight - _baseScreenHeight) > _win.innerHeight * 0.25) &&
            _resizeDelay.restart(true);
    },
    // ignore resizes triggered by refresh()
    _listeners = {},
    _emptyArray = [],
    _softRefresh = function _softRefresh() {
        return _removeListener(ScrollTrigger, 'scrollEnd', _softRefresh) || _refreshAll(true);
    },
    _dispatch = function _dispatch(type) {
        return (
            (_listeners[type] &&
                _listeners[type].map(function (f) {
                    return f();
                })) ||
            _emptyArray
        );
    },
    _savedStyles = [],
    // when ScrollTrigger.saveStyles() is called, the inline styles are recorded in this Array in a sequential format like [element, cssText, gsCache, media]. This keeps it very memory-efficient and fast to iterate through.
    _revertRecorded = function _revertRecorded(media) {
        for (var i = 0; i < _savedStyles.length; i += 5) {
            if (!media || (_savedStyles[i + 4] && _savedStyles[i + 4].query === media)) {
                _savedStyles[i].style.cssText = _savedStyles[i + 1];
                _savedStyles[i].getBBox && _savedStyles[i].setAttribute('transform', _savedStyles[i + 2] || '');
                _savedStyles[i + 3].uncache = 1;
            }
        }
    },
    _revertAll = function _revertAll(kill, media) {
        var trigger;

        for (_i = 0; _i < _triggers.length; _i++) {
            trigger = _triggers[_i];

            if (trigger && (!media || trigger._ctx === media)) {
                if (kill) {
                    trigger.kill(1);
                } else {
                    trigger.revert(true, true);
                }
            }
        }

        _isReverted = true;
        media && _revertRecorded(media);
        media || _dispatch('revert');
    },
    _clearScrollMemory = function _clearScrollMemory(scrollRestoration, force) {
        // zero-out all the recorded scroll positions. Don't use _triggers because if, for example, .matchMedia() is used to create some ScrollTriggers and then the user resizes and it removes ALL ScrollTriggers, and then go back to a size where there are ScrollTriggers, it would have kept the position(s) saved from the initial state.
        _scrollers.cache++;
        (force || !_refreshingAll) &&
            _scrollers.forEach(function (obj) {
                return _isFunction(obj) && obj.cacheID++ && (obj.rec = 0);
            });
        _isString(scrollRestoration) && (_win.history.scrollRestoration = _scrollRestoration = scrollRestoration);
    },
    _refreshingAll,
    _refreshID = 0,
    _queueRefreshID,
    _queueRefreshAll = function _queueRefreshAll() {
        // we don't want to call _refreshAll() every time we create a new ScrollTrigger (for performance reasons) - it's better to batch them. Some frameworks dynamically load content and we can't rely on the window's "load" or "DOMContentLoaded" events to trigger it.
        if (_queueRefreshID !== _refreshID) {
            var id = (_queueRefreshID = _refreshID);
            requestAnimationFrame(function () {
                return id === _refreshID && _refreshAll(true);
            });
        }
    },
    _refresh100vh = function _refresh100vh() {
        _body.appendChild(_div100vh);

        _100vh = (!_normalizer && _div100vh.offsetHeight) || _win.innerHeight;

        _body.removeChild(_div100vh);
    },
    _hideAllMarkers = function _hideAllMarkers(hide) {
        return _toArray(
            '.gsap-marker-start, .gsap-marker-end, .gsap-marker-scroller-start, .gsap-marker-scroller-end'
        ).forEach(function (el) {
            return (el.style.display = hide ? 'none' : 'block');
        });
    },
    _refreshAll = function _refreshAll(force, skipRevert) {
        if (_lastScrollTime && !force && !_isReverted) {
            _addListener(ScrollTrigger, 'scrollEnd', _softRefresh);

            return;
        }

        _refresh100vh();

        _refreshingAll = ScrollTrigger.isRefreshing = true;

        _scrollers.forEach(function (obj) {
            return _isFunction(obj) && ++obj.cacheID && (obj.rec = obj());
        }); // force the clearing of the cache because some browsers take a little while to dispatch the "scroll" event and the user may have changed the scroll position and then called ScrollTrigger.refresh() right away

        var refreshInits = _dispatch('refreshInit');

        _sort && ScrollTrigger.sort();
        skipRevert || _revertAll();

        _scrollers.forEach(function (obj) {
            if (_isFunction(obj)) {
                obj.smooth && (obj.target.style.scrollBehavior = 'auto'); // smooth scrolling interferes

                obj(0);
            }
        });

        _triggers.slice(0).forEach(function (t) {
            return t.refresh();
        }); // don't loop with _i because during a refresh() someone could call ScrollTrigger.update() which would iterate through _i resulting in a skip.

        _isReverted = false;

        _triggers.forEach(function (t) {
            // nested pins (pinnedContainer) with pinSpacing may expand the container, so we must accommodate that here.
            if (t._subPinOffset && t.pin) {
                var prop = t.vars.horizontal ? 'offsetWidth' : 'offsetHeight',
                    original = t.pin[prop];
                t.revert(true, 1);
                t.adjustPinSpacing(t.pin[prop] - original);
                t.refresh();
            }
        });

        _clampingMax = 1; // pinSpacing might be propping a page open, thus when we .setPositions() to clamp a ScrollTrigger's end we should leave the pinSpacing alone. That's what this flag is for.

        _hideAllMarkers(true);

        _triggers.forEach(function (t) {
            // the scroller's max scroll position may change after all the ScrollTriggers refreshed (like pinning could push it down), so we need to loop back and correct any with end: "max". Same for anything with a clamped end
            var max = _maxScroll(t.scroller, t._dir),
                endClamp = t.vars.end === 'max' || (t._endClamp && t.end > max),
                startClamp = t._startClamp && t.start >= max;

            (endClamp || startClamp) &&
                t.setPositions(
                    startClamp ? max - 1 : t.start,
                    endClamp ? Math.max(startClamp ? max : t.start + 1, max) : t.end,
                    true
                );
        });

        _hideAllMarkers(false);

        _clampingMax = 0;
        refreshInits.forEach(function (result) {
            return result && result.render && result.render(-1);
        }); // if the onRefreshInit() returns an animation (typically a gsap.set()), revert it. This makes it easy to put things in a certain spot before refreshing for measurement purposes, and then put things back.

        _scrollers.forEach(function (obj) {
            if (_isFunction(obj)) {
                obj.smooth &&
                    requestAnimationFrame(function () {
                        return (obj.target.style.scrollBehavior = 'smooth');
                    });
                obj.rec && obj(obj.rec);
            }
        });

        _clearScrollMemory(_scrollRestoration, 1);

        _resizeDelay.pause();

        _refreshID++;
        _refreshingAll = 2;

        _updateAll(2);

        _triggers.forEach(function (t) {
            return _isFunction(t.vars.onRefresh) && t.vars.onRefresh(t);
        });

        _refreshingAll = ScrollTrigger.isRefreshing = false;

        _dispatch('refresh');
    },
    _lastScroll = 0,
    _direction = 1,
    _primary,
    _updateAll = function _updateAll(force) {
        if (force === 2 || (!_refreshingAll && !_isReverted)) {
            // _isReverted could be true if, for example, a matchMedia() is in the process of executing. We don't want to update during the time everything is reverted.
            ScrollTrigger.isUpdating = true;
            _primary && _primary.update(0); // ScrollSmoother uses refreshPriority -9999 to become the primary that gets updated before all others because it affects the scroll position.

            var l = _triggers.length,
                time = _getTime(),
                recordVelocity = time - _time1 >= 50,
                scroll = l && _triggers[0].scroll();

            _direction = _lastScroll > scroll ? -1 : 1;
            _refreshingAll || (_lastScroll = scroll);

            if (recordVelocity) {
                if (_lastScrollTime && !_pointerIsDown && time - _lastScrollTime > 200) {
                    _lastScrollTime = 0;

                    _dispatch('scrollEnd');
                }

                _time2 = _time1;
                _time1 = time;
            }

            if (_direction < 0) {
                _i = l;

                while (_i-- > 0) {
                    _triggers[_i] && _triggers[_i].update(0, recordVelocity);
                }

                _direction = 1;
            } else {
                for (_i = 0; _i < l; _i++) {
                    _triggers[_i] && _triggers[_i].update(0, recordVelocity);
                }
            }

            ScrollTrigger.isUpdating = false;
        }

        _rafID = 0;
    },
    _propNamesToCopy = [
        _left,
        _top,
        _bottom,
        _right,
        _margin + _Bottom,
        _margin + _Right,
        _margin + _Top,
        _margin + _Left,
        'display',
        'flexShrink',
        'float',
        'zIndex',
        'gridColumnStart',
        'gridColumnEnd',
        'gridRowStart',
        'gridRowEnd',
        'gridArea',
        'justifySelf',
        'alignSelf',
        'placeSelf',
        'order'
    ],
    _stateProps = _propNamesToCopy.concat([
        _width,
        _height,
        'boxSizing',
        'max' + _Width,
        'max' + _Height,
        'position',
        _margin,
        _padding,
        _padding + _Top,
        _padding + _Right,
        _padding + _Bottom,
        _padding + _Left
    ]),
    _swapPinOut = function _swapPinOut(pin, spacer, state) {
        _setState(state);

        var cache = pin._gsap;

        if (cache.spacerIsNative) {
            _setState(cache.spacerState);
        } else if (pin._gsap.swappedIn) {
            var parent = spacer.parentNode;

            if (parent) {
                parent.insertBefore(pin, spacer);
                parent.removeChild(spacer);
            }
        }

        pin._gsap.swappedIn = false;
    },
    _swapPinIn = function _swapPinIn(pin, spacer, cs, spacerState) {
        if (!pin._gsap.swappedIn) {
            var i = _propNamesToCopy.length,
                spacerStyle = spacer.style,
                pinStyle = pin.style,
                p;

            while (i--) {
                p = _propNamesToCopy[i];
                spacerStyle[p] = cs[p];
            }

            spacerStyle.position = cs.position === 'absolute' ? 'absolute' : 'relative';
            cs.display === 'inline' && (spacerStyle.display = 'inline-block');
            pinStyle[_bottom] = pinStyle[_right] = 'auto';
            spacerStyle.flexBasis = cs.flexBasis || 'auto';
            spacerStyle.overflow = 'visible';
            spacerStyle.boxSizing = 'border-box';
            spacerStyle[_width] = _getSize(pin, _horizontal) + _px;
            spacerStyle[_height] = _getSize(pin, _vertical) + _px;
            spacerStyle[_padding] = pinStyle[_margin] = pinStyle[_top] = pinStyle[_left] = '0';

            _setState(spacerState);

            pinStyle[_width] = pinStyle['max' + _Width] = cs[_width];
            pinStyle[_height] = pinStyle['max' + _Height] = cs[_height];
            pinStyle[_padding] = cs[_padding];

            if (pin.parentNode !== spacer) {
                pin.parentNode.insertBefore(spacer, pin);
                spacer.appendChild(pin);
            }

            pin._gsap.swappedIn = true;
        }
    },
    _capsExp = /([A-Z])/g,
    _setState = function _setState(state) {
        if (state) {
            var style = state.t.style,
                l = state.length,
                i = 0,
                p,
                value;
            (state.t._gsap || gsap.core.getCache(state.t)).uncache = 1; // otherwise transforms may be off

            for (; i < l; i += 2) {
                value = state[i + 1];
                p = state[i];

                if (value) {
                    style[p] = value;
                } else if (style[p]) {
                    style.removeProperty(p.replace(_capsExp, '-$1').toLowerCase());
                }
            }
        }
    },
    _getState = function _getState(element) {
        // returns an Array with alternating values like [property, value, property, value] and a "t" property pointing to the target (element). Makes it fast and cheap.
        var l = _stateProps.length,
            style = element.style,
            state = [],
            i = 0;

        for (; i < l; i++) {
            state.push(_stateProps[i], style[_stateProps[i]]);
        }

        state.t = element;
        return state;
    },
    _copyState = function _copyState(state, override, omitOffsets) {
        var result = [],
            l = state.length,
            i = omitOffsets ? 8 : 0,
            // skip top, left, right, bottom if omitOffsets is true
            p;

        for (; i < l; i += 2) {
            p = state[i];
            result.push(p, p in override ? override[p] : state[i + 1]);
        }

        result.t = state.t;
        return result;
    },
    _winOffsets = {
        left: 0,
        top: 0
    },
    // // potential future feature (?) Allow users to calculate where a trigger hits (scroll position) like getScrollPosition("#id", "top bottom")
    // _getScrollPosition = (trigger, position, {scroller, containerAnimation, horizontal}) => {
    // 	scroller = _getTarget(scroller || _win);
    // 	let direction = horizontal ? _horizontal : _vertical,
    // 		isViewport = _isViewport(scroller);
    // 	_getSizeFunc(scroller, isViewport, direction);
    // 	return _parsePosition(position, _getTarget(trigger), _getSizeFunc(scroller, isViewport, direction)(), direction, _getScrollFunc(scroller, direction)(), 0, 0, 0, _getOffsetsFunc(scroller, isViewport)(), isViewport ? 0 : parseFloat(_getComputedStyle(scroller)["border" + direction.p2 + _Width]) || 0, 0, containerAnimation ? containerAnimation.duration() : _maxScroll(scroller), containerAnimation);
    // },
    _parsePosition = function _parsePosition(
        value,
        trigger,
        scrollerSize,
        direction,
        scroll,
        marker,
        markerScroller,
        self,
        scrollerBounds,
        borderWidth,
        useFixedPosition,
        scrollerMax,
        containerAnimation,
        clampZeroProp
    ) {
        _isFunction(value) && (value = value(self));

        if (_isString(value) && value.substr(0, 3) === 'max') {
            value = scrollerMax + (value.charAt(4) === '=' ? _offsetToPx('0' + value.substr(3), scrollerSize) : 0);
        }

        var time = containerAnimation ? containerAnimation.time() : 0,
            p1,
            p2,
            element;
        containerAnimation && containerAnimation.seek(0);
        isNaN(value) || (value = +value); // convert a string number like "45" to an actual number

        if (!_isNumber(value)) {
            _isFunction(trigger) && (trigger = trigger(self));
            var offsets = (value || '0').split(' '),
                bounds,
                localOffset,
                globalOffset,
                display;
            element = _getTarget(trigger, self) || _body;
            bounds = _getBounds(element) || {};

            if ((!bounds || (!bounds.left && !bounds.top)) && _getComputedStyle(element).display === 'none') {
                // if display is "none", it won't report getBoundingClientRect() properly
                display = element.style.display;
                element.style.display = 'block';
                bounds = _getBounds(element);
                display ? (element.style.display = display) : element.style.removeProperty('display');
            }

            localOffset = _offsetToPx(offsets[0], bounds[direction.d]);
            globalOffset = _offsetToPx(offsets[1] || '0', scrollerSize);
            value =
                bounds[direction.p] - scrollerBounds[direction.p] - borderWidth + localOffset + scroll - globalOffset;
            markerScroller &&
                _positionMarker(
                    markerScroller,
                    globalOffset,
                    direction,
                    scrollerSize - globalOffset < 20 || (markerScroller._isStart && globalOffset > 20)
                );
            scrollerSize -= scrollerSize - globalOffset; // adjust for the marker
        } else {
            containerAnimation &&
                (value = gsap.utils.mapRange(
                    containerAnimation.scrollTrigger.start,
                    containerAnimation.scrollTrigger.end,
                    0,
                    scrollerMax,
                    value
                ));
            markerScroller && _positionMarker(markerScroller, scrollerSize, direction, true);
        }

        if (clampZeroProp) {
            self[clampZeroProp] = value || -0.001;
            value < 0 && (value = 0);
        }

        if (marker) {
            var position = value + scrollerSize,
                isStart = marker._isStart;
            p1 = 'scroll' + direction.d2;

            _positionMarker(
                marker,
                position,
                direction,
                (isStart && position > 20) ||
                    (!isStart &&
                        (useFixedPosition ? Math.max(_body[p1], _docEl[p1]) : marker.parentNode[p1]) <= position + 1)
            );

            if (useFixedPosition) {
                scrollerBounds = _getBounds(markerScroller);
                useFixedPosition &&
                    (marker.style[direction.op.p] =
                        scrollerBounds[direction.op.p] - direction.op.m - marker._offset + _px);
            }
        }

        if (containerAnimation && element) {
            p1 = _getBounds(element);
            containerAnimation.seek(scrollerMax);
            p2 = _getBounds(element);
            containerAnimation._caScrollDist = p1[direction.p] - p2[direction.p];
            value = (value / containerAnimation._caScrollDist) * scrollerMax;
        }

        containerAnimation && containerAnimation.seek(time);
        return containerAnimation ? value : Math.round(value);
    },
    _prefixExp = /(webkit|moz|length|cssText|inset)/i,
    _reparent = function _reparent(element, parent, top, left) {
        if (element.parentNode !== parent) {
            var style = element.style,
                p,
                cs;

            if (parent === _body) {
                element._stOrig = style.cssText; // record original inline styles so we can revert them later

                cs = _getComputedStyle(element);

                for (p in cs) {
                    // must copy all relevant styles to ensure that nothing changes visually when we reparent to the <body>. Skip the vendor prefixed ones.
                    if (!+p && !_prefixExp.test(p) && cs[p] && typeof style[p] === 'string' && p !== '0') {
                        style[p] = cs[p];
                    }
                }

                style.top = top;
                style.left = left;
            } else {
                style.cssText = element._stOrig;
            }

            gsap.core.getCache(element).uncache = 1;
            parent.appendChild(element);
        }
    },
    _interruptionTracker = function _interruptionTracker(getValueFunc, initialValue, onInterrupt) {
        var last1 = initialValue,
            last2 = last1;
        return function (value) {
            var current = Math.round(getValueFunc()); // round because in some [very uncommon] Windows environments, scroll can get reported with decimals even though it was set without.

            if (
                current !== last1 &&
                current !== last2 &&
                Math.abs(current - last1) > 3 &&
                Math.abs(current - last2) > 3
            ) {
                // if the user scrolls, kill the tween. iOS Safari intermittently misreports the scroll position, it may be the most recently-set one or the one before that! When Safari is zoomed (CMD-+), it often misreports as 1 pixel off too! So if we set the scroll position to 125, for example, it'll actually report it as 124.
                value = current;
                onInterrupt && onInterrupt();
            }

            last2 = last1;
            last1 = value;
            return value;
        };
    },
    _shiftMarker = function _shiftMarker(marker, direction, value) {
        var vars = {};
        vars[direction.p] = '+=' + value;
        gsap.set(marker, vars);
    },
    // _mergeAnimations = animations => {
    // 	let tl = gsap.timeline({smoothChildTiming: true}).startTime(Math.min(...animations.map(a => a.globalTime(0))));
    // 	animations.forEach(a => {let time = a.totalTime(); tl.add(a); a.totalTime(time); });
    // 	tl.smoothChildTiming = false;
    // 	return tl;
    // },
    // returns a function that can be used to tween the scroll position in the direction provided, and when doing so it'll add a .tween property to the FUNCTION itself, and remove it when the tween completes or gets killed. This gives us a way to have multiple ScrollTriggers use a central function for any given scroller and see if there's a scroll tween running (which would affect if/how things get updated)
    _getTweenCreator = function _getTweenCreator(scroller, direction) {
        var getScroll = _getScrollFunc(scroller, direction),
            prop = '_scroll' + direction.p2,
            // add a tweenable property to the scroller that's a getter/setter function, like _scrollTop or _scrollLeft. This way, if someone does gsap.killTweensOf(scroller) it'll kill the scroll tween.
            getTween = function getTween(scrollTo, vars, initialValue, change1, change2) {
                var tween = getTween.tween,
                    onComplete = vars.onComplete,
                    modifiers = {};
                initialValue = initialValue || getScroll();

                var checkForInterruption = _interruptionTracker(getScroll, initialValue, function () {
                    tween.kill();
                    getTween.tween = 0;
                });

                change2 = (change1 && change2) || 0; // if change1 is 0, we set that to the difference and ignore change2. Otherwise, there would be a compound effect.

                change1 = change1 || scrollTo - initialValue;
                tween && tween.kill();
                vars[prop] = scrollTo;
                vars.inherit = false;
                vars.modifiers = modifiers;

                modifiers[prop] = function () {
                    return checkForInterruption(
                        initialValue + change1 * tween.ratio + change2 * tween.ratio * tween.ratio
                    );
                };

                vars.onUpdate = function () {
                    _scrollers.cache++;
                    getTween.tween && _updateAll(); // if it was interrupted/killed, like in a context.revert(), don't force an updateAll()
                };

                vars.onComplete = function () {
                    getTween.tween = 0;
                    onComplete && onComplete.call(tween);
                };

                tween = getTween.tween = gsap.to(scroller, vars);
                return tween;
            };

        scroller[prop] = getScroll;

        getScroll.wheelHandler = function () {
            return getTween.tween && getTween.tween.kill() && (getTween.tween = 0);
        };

        _addListener(scroller, 'wheel', getScroll.wheelHandler); // Windows machines handle mousewheel scrolling in chunks (like "3 lines per scroll") meaning the typical strategy for cancelling the scroll isn't as sensitive. It's much more likely to match one of the previous 2 scroll event positions. So we kill any snapping as soon as there's a wheel event.

        ScrollTrigger.isTouch && _addListener(scroller, 'touchmove', getScroll.wheelHandler);
        return getTween;
    };

export var ScrollTrigger = /*#__PURE__*/ (function () {
    function ScrollTrigger(vars, animation) {
        _coreInitted || ScrollTrigger.register(gsap) || console.warn('Please gsap.registerPlugin(ScrollTrigger)');

        _context(this);

        this.init(vars, animation);
    }

    var _proto = ScrollTrigger.prototype;

    _proto.init = function init(vars, animation) {
        this.progress = this.start = 0;
        this.vars && this.kill(true, true); // in case it's being initted again

        if (!_enabled) {
            this.update = this.refresh = this.kill = _passThrough;
            return;
        }

        vars = _setDefaults(
            _isString(vars) || _isNumber(vars) || vars.nodeType
                ? {
                      trigger: vars
                  }
                : vars,
            _defaults
        );

        var _vars = vars,
            onUpdate = _vars.onUpdate,
            toggleClass = _vars.toggleClass,
            id = _vars.id,
            onToggle = _vars.onToggle,
            onRefresh = _vars.onRefresh,
            scrub = _vars.scrub,
            trigger = _vars.trigger,
            pin = _vars.pin,
            pinSpacing = _vars.pinSpacing,
            invalidateOnRefresh = _vars.invalidateOnRefresh,
            anticipatePin = _vars.anticipatePin,
            onScrubComplete = _vars.onScrubComplete,
            onSnapComplete = _vars.onSnapComplete,
            once = _vars.once,
            snap = _vars.snap,
            pinReparent = _vars.pinReparent,
            pinSpacer = _vars.pinSpacer,
            containerAnimation = _vars.containerAnimation,
            fastScrollEnd = _vars.fastScrollEnd,
            preventOverlaps = _vars.preventOverlaps,
            direction =
                vars.horizontal || (vars.containerAnimation && vars.horizontal !== false) ? _horizontal : _vertical,
            isToggle = !scrub && scrub !== 0,
            scroller = _getTarget(vars.scroller || _win),
            scrollerCache = gsap.core.getCache(scroller),
            isViewport = _isViewport(scroller),
            useFixedPosition =
                ('pinType' in vars ? vars.pinType : _getProxyProp(scroller, 'pinType') || (isViewport && 'fixed')) ===
                'fixed',
            callbacks = [vars.onEnter, vars.onLeave, vars.onEnterBack, vars.onLeaveBack],
            toggleActions = isToggle && vars.toggleActions.split(' '),
            markers = 'markers' in vars ? vars.markers : _defaults.markers,
            borderWidth = isViewport
                ? 0
                : parseFloat(_getComputedStyle(scroller)['border' + direction.p2 + _Width]) || 0,
            self = this,
            onRefreshInit =
                vars.onRefreshInit &&
                function () {
                    return vars.onRefreshInit(self);
                },
            getScrollerSize = _getSizeFunc(scroller, isViewport, direction),
            getScrollerOffsets = _getOffsetsFunc(scroller, isViewport),
            lastSnap = 0,
            lastRefresh = 0,
            prevProgress = 0,
            scrollFunc = _getScrollFunc(scroller, direction),
            tweenTo,
            pinCache,
            snapFunc,
            scroll1,
            scroll2,
            start,
            end,
            markerStart,
            markerEnd,
            markerStartTrigger,
            markerEndTrigger,
            markerVars,
            executingOnRefresh,
            change,
            pinOriginalState,
            pinActiveState,
            pinState,
            spacer,
            offset,
            pinGetter,
            pinSetter,
            pinStart,
            pinChange,
            spacingStart,
            spacerState,
            markerStartSetter,
            pinMoves,
            markerEndSetter,
            cs,
            snap1,
            snap2,
            scrubTween,
            scrubSmooth,
            snapDurClamp,
            snapDelayedCall,
            prevScroll,
            prevAnimProgress,
            caMarkerSetter,
            customRevertReturn; // for the sake of efficiency, _startClamp/_endClamp serve like a truthy value indicating that clamping was enabled on the start/end, and ALSO store the actual pre-clamped numeric value. We tap into that in ScrollSmoother for speed effects. So for example, if start="clamp(top bottom)" results in a start of -100 naturally, it would get clamped to 0 but -100 would be stored in _startClamp.

        self._startClamp = self._endClamp = false;
        self._dir = direction;
        anticipatePin *= 45;
        self.scroller = scroller;
        self.scroll = containerAnimation ? containerAnimation.time.bind(containerAnimation) : scrollFunc;
        scroll1 = scrollFunc();
        self.vars = vars;
        animation = animation || vars.animation;

        if ('refreshPriority' in vars) {
            _sort = 1;
            vars.refreshPriority === -9999 && (_primary = self); // used by ScrollSmoother
        }

        scrollerCache.tweenScroll = scrollerCache.tweenScroll || {
            top: _getTweenCreator(scroller, _vertical),
            left: _getTweenCreator(scroller, _horizontal)
        };
        self.tweenTo = tweenTo = scrollerCache.tweenScroll[direction.p];

        self.scrubDuration = function (value) {
            scrubSmooth = _isNumber(value) && value;

            if (!scrubSmooth) {
                scrubTween && scrubTween.progress(1).kill();
                scrubTween = 0;
            } else {
                scrubTween
                    ? scrubTween.duration(value)
                    : (scrubTween = gsap.to(animation, {
                          ease: 'expo',
                          totalProgress: '+=0',
                          inherit: false,
                          duration: scrubSmooth,
                          paused: true,
                          onComplete: function onComplete() {
                              return onScrubComplete && onScrubComplete(self);
                          }
                      }));
            }
        };

        if (animation) {
            animation.vars.lazy = false;
            (animation._initted && !self.isReverted) ||
                (animation.vars.immediateRender !== false &&
                    vars.immediateRender !== false &&
                    animation.duration() &&
                    animation.render(0, true, true)); // special case: if this ScrollTrigger gets re-initted, a from() tween with a stagger could get initted initially and then reverted on the re-init which means it'll need to get rendered again here to properly display things. Otherwise, See https://gsap.com/forums/topic/36777-scrollsmoother-splittext-nextjs/ and https://codepen.io/GreenSock/pen/eYPyPpd?editors=0010

            self.animation = animation.pause();
            animation.scrollTrigger = self;
            self.scrubDuration(scrub);
            snap1 = 0;
            id || (id = animation.vars.id);
        }

        if (snap) {
            // TODO: potential idea: use legitimate CSS scroll snapping by pushing invisible elements into the DOM that serve as snap positions, and toggle the document.scrollingElement.style.scrollSnapType onToggle. See https://codepen.io/GreenSock/pen/JjLrgWM for a quick proof of concept.
            if (!_isObject(snap) || snap.push) {
                snap = {
                    snapTo: snap
                };
            }

            'scrollBehavior' in _body.style &&
                gsap.set(isViewport ? [_body, _docEl] : scroller, {
                    scrollBehavior: 'auto'
                }); // smooth scrolling doesn't work with snap.

            _scrollers.forEach(function (o) {
                return (
                    _isFunction(o) &&
                    o.target === (isViewport ? _doc.scrollingElement || _docEl : scroller) &&
                    (o.smooth = false)
                );
            }); // note: set smooth to false on both the vertical and horizontal scroll getters/setters

            snapFunc = _isFunction(snap.snapTo)
                ? snap.snapTo
                : snap.snapTo === 'labels'
                ? _getClosestLabel(animation)
                : snap.snapTo === 'labelsDirectional'
                ? _getLabelAtDirection(animation)
                : snap.directional !== false
                ? function (value, st) {
                      return _snapDirectional(snap.snapTo)(value, _getTime() - lastRefresh < 500 ? 0 : st.direction);
                  }
                : gsap.utils.snap(snap.snapTo);
            snapDurClamp = snap.duration || {
                min: 0.1,
                max: 2
            };
            snapDurClamp = _isObject(snapDurClamp)
                ? _clamp(snapDurClamp.min, snapDurClamp.max)
                : _clamp(snapDurClamp, snapDurClamp);
            snapDelayedCall = gsap
                .delayedCall(snap.delay || scrubSmooth / 2 || 0.1, function () {
                    var scroll = scrollFunc(),
                        refreshedRecently = _getTime() - lastRefresh < 500,
                        tween = tweenTo.tween;

                    if (
                        (refreshedRecently || Math.abs(self.getVelocity()) < 10) &&
                        !tween &&
                        !_pointerIsDown &&
                        lastSnap !== scroll
                    ) {
                        var progress = (scroll - start) / change,
                            totalProgress = animation && !isToggle ? animation.totalProgress() : progress,
                            velocity = refreshedRecently
                                ? 0
                                : ((totalProgress - snap2) / (_getTime() - _time2)) * 1000 || 0,
                            change1 = gsap.utils.clamp(
                                -progress,
                                1 - progress,
                                (_abs(velocity / 2) * velocity) / 0.185
                            ),
                            naturalEnd = progress + (snap.inertia === false ? 0 : change1),
                            endValue,
                            endScroll,
                            _snap = snap,
                            onStart = _snap.onStart,
                            _onInterrupt = _snap.onInterrupt,
                            _onComplete = _snap.onComplete;
                        endValue = snapFunc(naturalEnd, self);
                        _isNumber(endValue) || (endValue = naturalEnd); // in case the function didn't return a number, fall back to using the naturalEnd

                        endScroll = Math.round(start + endValue * change);

                        if (scroll <= end && scroll >= start && endScroll !== scroll) {
                            if (tween && !tween._initted && tween.data <= _abs(endScroll - scroll)) {
                                // there's an overlapping snap! So we must figure out which one is closer and let that tween live.
                                return;
                            }

                            if (snap.inertia === false) {
                                change1 = endValue - progress;
                            }

                            tweenTo(
                                endScroll,
                                {
                                    duration: snapDurClamp(
                                        _abs(
                                            (Math.max(
                                                _abs(naturalEnd - totalProgress),
                                                _abs(endValue - totalProgress)
                                            ) *
                                                0.185) /
                                                velocity /
                                                0.05 || 0
                                        )
                                    ),
                                    ease: snap.ease || 'power3',
                                    data: _abs(endScroll - scroll),
                                    // record the distance so that if another snap tween occurs (conflict) we can prioritize the closest snap.
                                    onInterrupt: function onInterrupt() {
                                        return snapDelayedCall.restart(true) && _onInterrupt && _onInterrupt(self);
                                    },
                                    onComplete: function onComplete() {
                                        self.update();
                                        lastSnap = scrollFunc();

                                        if (animation) {
                                            // the resolution of the scrollbar is limited, so we should correct the scrubbed animation's playhead at the end to match EXACTLY where it was supposed to snap
                                            scrubTween
                                                ? scrubTween.resetTo(
                                                      'totalProgress',
                                                      endValue,
                                                      animation._tTime / animation._tDur
                                                  )
                                                : animation.progress(endValue);
                                        }

                                        snap1 = snap2 =
                                            animation && !isToggle ? animation.totalProgress() : self.progress;
                                        onSnapComplete && onSnapComplete(self);
                                        _onComplete && _onComplete(self);
                                    }
                                },
                                scroll,
                                change1 * change,
                                endScroll - scroll - change1 * change
                            );
                            onStart && onStart(self, tweenTo.tween);
                        }
                    } else if (self.isActive && lastSnap !== scroll) {
                        snapDelayedCall.restart(true);
                    }
                })
                .pause();
        }

        id && (_ids[id] = self);
        trigger = self.trigger = _getTarget(trigger || (pin !== true && pin)); // if a trigger has some kind of scroll-related effect applied that could contaminate the "y" or "x" position (like a ScrollSmoother effect), we needed a way to temporarily revert it, so we use the stRevert property of the gsCache. It can return another function that we'll call at the end so it can return to its normal state.

        customRevertReturn = trigger && trigger._gsap && trigger._gsap.stRevert;
        customRevertReturn && (customRevertReturn = customRevertReturn(self));
        pin = pin === true ? trigger : _getTarget(pin);
        _isString(toggleClass) &&
            (toggleClass = {
                targets: trigger,
                className: toggleClass
            });

        if (pin) {
            pinSpacing === false ||
                pinSpacing === _margin ||
                (pinSpacing =
                    !pinSpacing &&
                    pin.parentNode &&
                    pin.parentNode.style &&
                    _getComputedStyle(pin.parentNode).display === 'flex'
                        ? false
                        : _padding); // if the parent is display: flex, don't apply pinSpacing by default. We should check that pin.parentNode is an element (not shadow dom window)

            self.pin = pin;
            pinCache = gsap.core.getCache(pin);

            if (!pinCache.spacer) {
                // record the spacer and pinOriginalState on the cache in case someone tries pinning the same element with MULTIPLE ScrollTriggers - we don't want to have multiple spacers or record the "original" pin state after it has already been affected by another ScrollTrigger.
                if (pinSpacer) {
                    pinSpacer = _getTarget(pinSpacer);
                    pinSpacer && !pinSpacer.nodeType && (pinSpacer = pinSpacer.current || pinSpacer.nativeElement); // for React & Angular

                    pinCache.spacerIsNative = !!pinSpacer;
                    pinSpacer && (pinCache.spacerState = _getState(pinSpacer));
                }

                pinCache.spacer = spacer = pinSpacer || _doc.createElement('div');
                spacer.classList.add('pin-spacer');
                id && spacer.classList.add('pin-spacer-' + id);
                pinCache.pinState = pinOriginalState = _getState(pin);
            } else {
                pinOriginalState = pinCache.pinState;
            }

            vars.force3D !== false &&
                gsap.set(pin, {
                    force3D: true
                });
            self.spacer = spacer = pinCache.spacer;
            cs = _getComputedStyle(pin);
            spacingStart = cs[pinSpacing + direction.os2];
            pinGetter = gsap.getProperty(pin);
            pinSetter = gsap.quickSetter(pin, direction.a, _px); // pin.firstChild && !_maxScroll(pin, direction) && (pin.style.overflow = "hidden"); // protects from collapsing margins, but can have unintended consequences as demonstrated here: https://codepen.io/GreenSock/pen/1e42c7a73bfa409d2cf1e184e7a4248d so it was removed in favor of just telling people to set up their CSS to avoid the collapsing margins (overflow: hidden | auto is just one option. Another is border-top: 1px solid transparent).

            _swapPinIn(pin, spacer, cs);

            pinState = _getState(pin);
        }

        if (markers) {
            markerVars = _isObject(markers) ? _setDefaults(markers, _markerDefaults) : _markerDefaults;
            markerStartTrigger = _createMarker('scroller-start', id, scroller, direction, markerVars, 0);
            markerEndTrigger = _createMarker(
                'scroller-end',
                id,
                scroller,
                direction,
                markerVars,
                0,
                markerStartTrigger
            );
            offset = markerStartTrigger['offset' + direction.op.d2];

            var content = _getTarget(_getProxyProp(scroller, 'content') || scroller);

            markerStart = this.markerStart = _createMarker(
                'start',
                id,
                content,
                direction,
                markerVars,
                offset,
                0,
                containerAnimation
            );
            markerEnd = this.markerEnd = _createMarker(
                'end',
                id,
                content,
                direction,
                markerVars,
                offset,
                0,
                containerAnimation
            );
            containerAnimation && (caMarkerSetter = gsap.quickSetter([markerStart, markerEnd], direction.a, _px));

            if (!useFixedPosition && !(_proxies.length && _getProxyProp(scroller, 'fixedMarkers') === true)) {
                _makePositionable(isViewport ? _body : scroller);

                gsap.set([markerStartTrigger, markerEndTrigger], {
                    force3D: true
                });
                markerStartSetter = gsap.quickSetter(markerStartTrigger, direction.a, _px);
                markerEndSetter = gsap.quickSetter(markerEndTrigger, direction.a, _px);
            }
        }

        if (containerAnimation) {
            var oldOnUpdate = containerAnimation.vars.onUpdate,
                oldParams = containerAnimation.vars.onUpdateParams;
            containerAnimation.eventCallback('onUpdate', function () {
                self.update(0, 0, 1);
                oldOnUpdate && oldOnUpdate.apply(containerAnimation, oldParams || []);
            });
        }

        self.previous = function () {
            return _triggers[_triggers.indexOf(self) - 1];
        };

        self.next = function () {
            return _triggers[_triggers.indexOf(self) + 1];
        };

        self.revert = function (revert, temp) {
            if (!temp) {
                return self.kill(true);
            } // for compatibility with gsap.context() and gsap.matchMedia() which call revert()

            var r = revert !== false || !self.enabled,
                prevRefreshing = _refreshing;

            if (r !== self.isReverted) {
                if (r) {
                    prevScroll = Math.max(scrollFunc(), self.scroll.rec || 0); // record the scroll so we can revert later (repositioning/pinning things can affect scroll position). In the static refresh() method, we first record all the scroll positions as a reference.

                    prevProgress = self.progress;
                    prevAnimProgress = animation && animation.progress();
                }

                markerStart &&
                    [markerStart, markerEnd, markerStartTrigger, markerEndTrigger].forEach(function (m) {
                        return (m.style.display = r ? 'none' : 'block');
                    });

                if (r) {
                    _refreshing = self;
                    self.update(r); // make sure the pin is back in its original position so that all the measurements are correct. do this BEFORE swapping the pin out
                }

                if (pin && (!pinReparent || !self.isActive)) {
                    if (r) {
                        _swapPinOut(pin, spacer, pinOriginalState);
                    } else {
                        _swapPinIn(pin, spacer, _getComputedStyle(pin), spacerState);
                    }
                }

                r || self.update(r); // when we're restoring, the update should run AFTER swapping the pin into its pin-spacer.

                _refreshing = prevRefreshing; // restore. We set it to true during the update() so that things fire properly in there.

                self.isReverted = r;
            }
        };

        self.refresh = function (soft, force, position, pinOffset) {
            // position is typically only defined if it's coming from setPositions() - it's a way to skip the normal parsing. pinOffset is also only from setPositions() and is mostly related to fancy stuff we need to do in ScrollSmoother with effects
            if ((_refreshing || !self.enabled) && !force) {
                return;
            }

            if (pin && soft && _lastScrollTime) {
                _addListener(ScrollTrigger, 'scrollEnd', _softRefresh);

                return;
            }

            !_refreshingAll && onRefreshInit && onRefreshInit(self);
            _refreshing = self;

            if (tweenTo.tween && !position) {
                // we skip this if a position is passed in because typically that's from .setPositions() and it's best to allow in-progress snapping to continue.
                tweenTo.tween.kill();
                tweenTo.tween = 0;
            }

            scrubTween && scrubTween.pause();
            invalidateOnRefresh &&
                animation &&
                animation
                    .revert({
                        kill: false
                    })
                    .invalidate();
            self.isReverted || self.revert(true, true);
            self._subPinOffset = false; // we'll set this to true in the sub-pins if we find any

            var size = getScrollerSize(),
                scrollerBounds = getScrollerOffsets(),
                max = containerAnimation ? containerAnimation.duration() : _maxScroll(scroller, direction),
                isFirstRefresh = change <= 0.01,
                offset = 0,
                otherPinOffset = pinOffset || 0,
                parsedEnd = _isObject(position) ? position.end : vars.end,
                parsedEndTrigger = vars.endTrigger || trigger,
                parsedStart = _isObject(position)
                    ? position.start
                    : vars.start || (vars.start === 0 || !trigger ? 0 : pin ? '0 0' : '0 100%'),
                pinnedContainer = (self.pinnedContainer =
                    vars.pinnedContainer && _getTarget(vars.pinnedContainer, self)),
                triggerIndex = (trigger && Math.max(0, _triggers.indexOf(self))) || 0,
                i = triggerIndex,
                cs,
                bounds,
                scroll,
                isVertical,
                override,
                curTrigger,
                curPin,
                oppositeScroll,
                initted,
                revertedPins,
                forcedOverflow,
                markerStartOffset,
                markerEndOffset;

            if (markers && _isObject(position)) {
                // if we alter the start/end positions with .setPositions(), it generally feeds in absolute NUMBERS which don't convey information about where to line up the markers, so to keep it intuitive, we record how far the trigger positions shift after applying the new numbers and then offset by that much in the opposite direction. We do the same to the associated trigger markers too of course.
                markerStartOffset = gsap.getProperty(markerStartTrigger, direction.p);
                markerEndOffset = gsap.getProperty(markerEndTrigger, direction.p);
            }

            while (i--) {
                // user might try to pin the same element more than once, so we must find any prior triggers with the same pin, revert them, and determine how long they're pinning so that we can offset things appropriately. Make sure we revert from last to first so that things "rewind" properly.
                curTrigger = _triggers[i];
                curTrigger.end || curTrigger.refresh(0, 1) || (_refreshing = self); // if it's a timeline-based trigger that hasn't been fully initialized yet because it's waiting for 1 tick, just force the refresh() here, otherwise if it contains a pin that's supposed to affect other ScrollTriggers further down the page, they won't be adjusted properly.

                curPin = curTrigger.pin;

                if (
                    curPin &&
                    (curPin === trigger || curPin === pin || curPin === pinnedContainer) &&
                    !curTrigger.isReverted
                ) {
                    revertedPins || (revertedPins = []);
                    revertedPins.unshift(curTrigger); // we'll revert from first to last to make sure things reach their end state properly

                    curTrigger.revert(true, true);
                }

                if (curTrigger !== _triggers[i]) {
                    // in case it got removed.
                    triggerIndex--;
                    i--;
                }
            }

            _isFunction(parsedStart) && (parsedStart = parsedStart(self));
            parsedStart = _parseClamp(parsedStart, 'start', self);
            start =
                _parsePosition(
                    parsedStart,
                    trigger,
                    size,
                    direction,
                    scrollFunc(),
                    markerStart,
                    markerStartTrigger,
                    self,
                    scrollerBounds,
                    borderWidth,
                    useFixedPosition,
                    max,
                    containerAnimation,
                    self._startClamp && '_startClamp'
                ) || (pin ? -0.001 : 0);
            _isFunction(parsedEnd) && (parsedEnd = parsedEnd(self));

            if (_isString(parsedEnd) && !parsedEnd.indexOf('+=')) {
                if (~parsedEnd.indexOf(' ')) {
                    parsedEnd = (_isString(parsedStart) ? parsedStart.split(' ')[0] : '') + parsedEnd;
                } else {
                    offset = _offsetToPx(parsedEnd.substr(2), size);
                    parsedEnd = _isString(parsedStart)
                        ? parsedStart
                        : (containerAnimation
                              ? gsap.utils.mapRange(
                                    0,
                                    containerAnimation.duration(),
                                    containerAnimation.scrollTrigger.start,
                                    containerAnimation.scrollTrigger.end,
                                    start
                                )
                              : start) + offset; // _parsePosition won't factor in the offset if the start is a number, so do it here.

                    parsedEndTrigger = trigger;
                }
            }

            parsedEnd = _parseClamp(parsedEnd, 'end', self);
            end =
                Math.max(
                    start,
                    _parsePosition(
                        parsedEnd || (parsedEndTrigger ? '100% 0' : max),
                        parsedEndTrigger,
                        size,
                        direction,
                        scrollFunc() + offset,
                        markerEnd,
                        markerEndTrigger,
                        self,
                        scrollerBounds,
                        borderWidth,
                        useFixedPosition,
                        max,
                        containerAnimation,
                        self._endClamp && '_endClamp'
                    )
                ) || -0.001;
            offset = 0;
            i = triggerIndex;

            while (i--) {
                curTrigger = _triggers[i];
                curPin = curTrigger.pin;

                if (
                    curPin &&
                    curTrigger.start - curTrigger._pinPush <= start &&
                    !containerAnimation &&
                    curTrigger.end > 0
                ) {
                    cs = curTrigger.end - (self._startClamp ? Math.max(0, curTrigger.start) : curTrigger.start);

                    if (
                        ((curPin === trigger && curTrigger.start - curTrigger._pinPush < start) ||
                            curPin === pinnedContainer) &&
                        isNaN(parsedStart)
                    ) {
                        // numeric start values shouldn't be offset at all - treat them as absolute
                        offset += cs * (1 - curTrigger.progress);
                    }

                    curPin === pin && (otherPinOffset += cs);
                }
            }

            start += offset;
            end += offset;
            self._startClamp && (self._startClamp += offset);

            if (self._endClamp && !_refreshingAll) {
                self._endClamp = end || -0.001;
                end = Math.min(end, _maxScroll(scroller, direction));
            }

            change = end - start || ((start -= 0.01) && 0.001);

            if (isFirstRefresh) {
                // on the very first refresh(), the prevProgress couldn't have been accurate yet because the start/end were never calculated, so we set it here. Before 3.11.5, it could lead to an inaccurate scroll position restoration with snapping.
                prevProgress = gsap.utils.clamp(0, 1, gsap.utils.normalize(start, end, prevScroll));
            }

            self._pinPush = otherPinOffset;

            if (markerStart && offset) {
                // offset the markers if necessary
                cs = {};
                cs[direction.a] = '+=' + offset;
                pinnedContainer && (cs[direction.p] = '-=' + scrollFunc());
                gsap.set([markerStart, markerEnd], cs);
            }

            if (pin && !(_clampingMax && self.end >= _maxScroll(scroller, direction))) {
                cs = _getComputedStyle(pin);
                isVertical = direction === _vertical;
                scroll = scrollFunc(); // recalculate because the triggers can affect the scroll

                pinStart = parseFloat(pinGetter(direction.a)) + otherPinOffset;

                if (!max && end > 1) {
                    // makes sure the scroller has a scrollbar, otherwise if something has width: 100%, for example, it would be too big (exclude the scrollbar). See https://gsap.com/forums/topic/25182-scrolltrigger-width-of-page-increase-where-markers-are-set-to-false/
                    forcedOverflow = (isViewport ? _doc.scrollingElement || _docEl : scroller).style;
                    forcedOverflow = {
                        style: forcedOverflow,
                        value: forcedOverflow['overflow' + direction.a.toUpperCase()]
                    };

                    if (isViewport && _getComputedStyle(_body)['overflow' + direction.a.toUpperCase()] !== 'scroll') {
                        // avoid an extra scrollbar if BOTH <html> and <body> have overflow set to "scroll"
                        forcedOverflow.style['overflow' + direction.a.toUpperCase()] = 'scroll';
                    }
                }

                _swapPinIn(pin, spacer, cs);

                pinState = _getState(pin); // transforms will interfere with the top/left/right/bottom placement, so remove them temporarily. getBoundingClientRect() factors in transforms.

                bounds = _getBounds(pin, true);
                oppositeScroll = useFixedPosition && _getScrollFunc(scroller, isVertical ? _horizontal : _vertical)();

                if (pinSpacing) {
                    spacerState = [pinSpacing + direction.os2, change + otherPinOffset + _px];
                    spacerState.t = spacer;
                    i = pinSpacing === _padding ? _getSize(pin, direction) + change + otherPinOffset : 0;

                    if (i) {
                        spacerState.push(direction.d, i + _px); // for box-sizing: border-box (must include padding).

                        spacer.style.flexBasis !== 'auto' && (spacer.style.flexBasis = i + _px);
                    }

                    _setState(spacerState);

                    if (pinnedContainer) {
                        // in ScrollTrigger.refresh(), we need to re-evaluate the pinContainer's size because this pinSpacing may stretch it out, but we can't just add the exact distance because depending on layout, it may not push things down or it may only do so partially.
                        _triggers.forEach(function (t) {
                            if (t.pin === pinnedContainer && t.vars.pinSpacing !== false) {
                                t._subPinOffset = true;
                            }
                        });
                    }

                    useFixedPosition && scrollFunc(prevScroll);
                } else {
                    i = _getSize(pin, direction);
                    i && spacer.style.flexBasis !== 'auto' && (spacer.style.flexBasis = i + _px);
                }

                if (useFixedPosition) {
                    override = {
                        top: bounds.top + (isVertical ? scroll - start : oppositeScroll) + _px,
                        left: bounds.left + (isVertical ? oppositeScroll : scroll - start) + _px,
                        boxSizing: 'border-box',
                        position: 'fixed'
                    };
                    override[_width] = override['max' + _Width] = Math.ceil(bounds.width) + _px;
                    override[_height] = override['max' + _Height] = Math.ceil(bounds.height) + _px;
                    override[_margin] =
                        override[_margin + _Top] =
                        override[_margin + _Right] =
                        override[_margin + _Bottom] =
                        override[_margin + _Left] =
                            '0';
                    override[_padding] = cs[_padding];
                    override[_padding + _Top] = cs[_padding + _Top];
                    override[_padding + _Right] = cs[_padding + _Right];
                    override[_padding + _Bottom] = cs[_padding + _Bottom];
                    override[_padding + _Left] = cs[_padding + _Left];
                    pinActiveState = _copyState(pinOriginalState, override, pinReparent);
                    _refreshingAll && scrollFunc(0);
                }

                if (animation) {
                    // the animation might be affecting the transform, so we must jump to the end, check the value, and compensate accordingly. Otherwise, when it becomes unpinned, the pinSetter() will get set to a value that doesn't include whatever the animation did.
                    initted = animation._initted; // if not, we must invalidate() after this step, otherwise it could lock in starting values prematurely.

                    _suppressOverwrites(1);

                    animation.render(animation.duration(), true, true);
                    pinChange = pinGetter(direction.a) - pinStart + change + otherPinOffset;
                    pinMoves = Math.abs(change - pinChange) > 1;
                    useFixedPosition && pinMoves && pinActiveState.splice(pinActiveState.length - 2, 2); // transform is the last property/value set in the state Array. Since the animation is controlling that, we should omit it.

                    animation.render(0, true, true);
                    initted || animation.invalidate(true);
                    animation.parent || animation.totalTime(animation.totalTime()); // if, for example, a toggleAction called play() and then refresh() happens and when we render(1) above, it would cause the animation to complete and get removed from its parent, so this makes sure it gets put back in.

                    _suppressOverwrites(0);
                } else {
                    pinChange = change;
                }

                forcedOverflow &&
                    (forcedOverflow.value
                        ? (forcedOverflow.style['overflow' + direction.a.toUpperCase()] = forcedOverflow.value)
                        : forcedOverflow.style.removeProperty('overflow-' + direction.a));
            } else if (trigger && scrollFunc() && !containerAnimation) {
                // it may be INSIDE a pinned element, so walk up the tree and look for any elements with _pinOffset to compensate because anything with pinSpacing that's already scrolled would throw off the measurements in getBoundingClientRect()
                bounds = trigger.parentNode;

                while (bounds && bounds !== _body) {
                    if (bounds._pinOffset) {
                        start -= bounds._pinOffset;
                        end -= bounds._pinOffset;
                    }

                    bounds = bounds.parentNode;
                }
            }

            revertedPins &&
                revertedPins.forEach(function (t) {
                    return t.revert(false, true);
                });
            self.start = start;
            self.end = end;
            scroll1 = scroll2 = _refreshingAll ? prevScroll : scrollFunc(); // reset velocity

            if (!containerAnimation && !_refreshingAll) {
                scroll1 < prevScroll && scrollFunc(prevScroll);
                self.scroll.rec = 0;
            }

            self.revert(false, true);
            lastRefresh = _getTime();

            if (snapDelayedCall) {
                lastSnap = -1; // just so snapping gets re-enabled, clear out any recorded last value
                // self.isActive && scrollFunc(start + change * prevProgress); // previously this line was here to ensure that when snapping kicks in, it's from the previous progress but in some cases that's not desirable, like an all-page ScrollTrigger when new content gets added to the page, that'd totally change the progress.

                snapDelayedCall.restart(true);
            }

            _refreshing = 0;
            animation &&
                isToggle &&
                (animation._initted || prevAnimProgress) &&
                animation.progress() !== prevAnimProgress &&
                animation.progress(prevAnimProgress || 0, true).render(animation.time(), true, true); // must force a re-render because if saveStyles() was used on the target(s), the styles could have been wiped out during the refresh().

            if (isFirstRefresh || prevProgress !== self.progress || containerAnimation || invalidateOnRefresh) {
                // ensures that the direction is set properly (when refreshing, progress is set back to 0 initially, then back again to wherever it needs to be) and that callbacks are triggered.
                animation &&
                    !isToggle &&
                    animation.totalProgress(
                        containerAnimation && start < -0.001 && !prevProgress
                            ? gsap.utils.normalize(start, end, 0)
                            : prevProgress,
                        true
                    ); // to avoid issues where animation callbacks like onStart aren't triggered.

                self.progress = isFirstRefresh || (scroll1 - start) / change === prevProgress ? 0 : prevProgress;
            }

            pin && pinSpacing && (spacer._pinOffset = Math.round(self.progress * pinChange));
            scrubTween && scrubTween.invalidate();

            if (!isNaN(markerStartOffset)) {
                // numbers were passed in for the position which are absolute, so instead of just putting the markers at the very bottom of the viewport, we figure out how far they shifted down (it's safe to assume they were originally positioned in closer relation to the trigger element with values like "top", "center", a percentage or whatever, so we offset that much in the opposite direction to basically revert them to the relative position thy were at previously.
                markerStartOffset -= gsap.getProperty(markerStartTrigger, direction.p);
                markerEndOffset -= gsap.getProperty(markerEndTrigger, direction.p);

                _shiftMarker(markerStartTrigger, direction, markerStartOffset);

                _shiftMarker(markerStart, direction, markerStartOffset - (pinOffset || 0));

                _shiftMarker(markerEndTrigger, direction, markerEndOffset);

                _shiftMarker(markerEnd, direction, markerEndOffset - (pinOffset || 0));
            }

            isFirstRefresh && !_refreshingAll && self.update(); // edge case - when you reload a page when it's already scrolled down, some browsers fire a "scroll" event before DOMContentLoaded, triggering an updateAll(). If we don't update the self.progress as part of refresh(), then when it happens next, it may record prevProgress as 0 when it really shouldn't, potentially causing a callback in an animation to fire again.

            if (onRefresh && !_refreshingAll && !executingOnRefresh) {
                // when refreshing all, we do extra work to correct pinnedContainer sizes and ensure things don't exceed the maxScroll, so we should do all the refreshes at the end after all that work so that the start/end values are corrected.
                executingOnRefresh = true;
                onRefresh(self);
                executingOnRefresh = false;
            }
        };

        self.getVelocity = function () {
            return ((scrollFunc() - scroll2) / (_getTime() - _time2)) * 1000 || 0;
        };

        self.endAnimation = function () {
            _endAnimation(self.callbackAnimation);

            if (animation) {
                scrubTween
                    ? scrubTween.progress(1)
                    : !animation.paused()
                    ? _endAnimation(animation, animation.reversed())
                    : isToggle || _endAnimation(animation, self.direction < 0, 1);
            }
        };

        self.labelToScroll = function (label) {
            return (
                (animation &&
                    animation.labels &&
                    (start || self.refresh() || start) + (animation.labels[label] / animation.duration()) * change) ||
                0
            );
        };

        self.getTrailing = function (name) {
            var i = _triggers.indexOf(self),
                a = self.direction > 0 ? _triggers.slice(0, i).reverse() : _triggers.slice(i + 1);

            return (
                _isString(name)
                    ? a.filter(function (t) {
                          return t.vars.preventOverlaps === name;
                      })
                    : a
            ).filter(function (t) {
                return self.direction > 0 ? t.end <= start : t.start >= end;
            });
        };

        self.update = function (reset, recordVelocity, forceFake) {
            if (containerAnimation && !forceFake && !reset) {
                return;
            }

            var scroll = _refreshingAll === true ? prevScroll : self.scroll(),
                p = reset ? 0 : (scroll - start) / change,
                clipped = p < 0 ? 0 : p > 1 ? 1 : p || 0,
                prevProgress = self.progress,
                isActive,
                wasActive,
                toggleState,
                action,
                stateChanged,
                toggled,
                isAtMax,
                isTakingAction;

            if (recordVelocity) {
                scroll2 = scroll1;
                scroll1 = containerAnimation ? scrollFunc() : scroll;

                if (snap) {
                    snap2 = snap1;
                    snap1 = animation && !isToggle ? animation.totalProgress() : clipped;
                }
            } // anticipate the pinning a few ticks ahead of time based on velocity to avoid a visual glitch due to the fact that most browsers do scrolling on a separate thread (not synced with requestAnimationFrame).

            if (anticipatePin && pin && !_refreshing && !_startup && _lastScrollTime) {
                if (!clipped && start < scroll + ((scroll - scroll2) / (_getTime() - _time2)) * anticipatePin) {
                    clipped = 0.0001;
                } else if (
                    clipped === 1 &&
                    end > scroll + ((scroll - scroll2) / (_getTime() - _time2)) * anticipatePin
                ) {
                    clipped = 0.9999;
                }
            }

            if (clipped !== prevProgress && self.enabled) {
                isActive = self.isActive = !!clipped && clipped < 1;
                wasActive = !!prevProgress && prevProgress < 1;
                toggled = isActive !== wasActive;
                stateChanged = toggled || !!clipped !== !!prevProgress; // could go from start all the way to end, thus it didn't toggle but it did change state in a sense (may need to fire a callback)

                self.direction = clipped > prevProgress ? 1 : -1;
                self.progress = clipped;

                if (stateChanged && !_refreshing) {
                    toggleState = clipped && !prevProgress ? 0 : clipped === 1 ? 1 : prevProgress === 1 ? 2 : 3; // 0 = enter, 1 = leave, 2 = enterBack, 3 = leaveBack (we prioritize the FIRST encounter, thus if you scroll really fast past the onEnter and onLeave in one tick, it'd prioritize onEnter.

                    if (isToggle) {
                        action =
                            (!toggled && toggleActions[toggleState + 1] !== 'none' && toggleActions[toggleState + 1]) ||
                            toggleActions[toggleState]; // if it didn't toggle, that means it shot right past and since we prioritize the "enter" action, we should switch to the "leave" in this case (but only if one is defined)

                        isTakingAction =
                            animation && (action === 'complete' || action === 'reset' || action in animation);
                    }
                }

                preventOverlaps &&
                    (toggled || isTakingAction) &&
                    (isTakingAction || scrub || !animation) &&
                    (_isFunction(preventOverlaps)
                        ? preventOverlaps(self)
                        : self.getTrailing(preventOverlaps).forEach(function (t) {
                              return t.endAnimation();
                          }));

                if (!isToggle) {
                    if (scrubTween && !_refreshing && !_startup) {
                        scrubTween._dp._time - scrubTween._start !== scrubTween._time &&
                            scrubTween.render(scrubTween._dp._time - scrubTween._start); // if there's a scrub on both the container animation and this one (or a ScrollSmoother), the update order would cause this one not to have rendered yet, so it wouldn't make any progress before we .restart() it heading toward the new progress so it'd appear stuck thus we force a render here.

                        if (scrubTween.resetTo) {
                            scrubTween.resetTo('totalProgress', clipped, animation._tTime / animation._tDur);
                        } else {
                            // legacy support (courtesy), before 3.10.0
                            scrubTween.vars.totalProgress = clipped;
                            scrubTween.invalidate().restart();
                        }
                    } else if (animation) {
                        animation.totalProgress(clipped, !!(_refreshing && (lastRefresh || reset)));
                    }
                }

                if (pin) {
                    reset && pinSpacing && (spacer.style[pinSpacing + direction.os2] = spacingStart);

                    if (!useFixedPosition) {
                        pinSetter(_round(pinStart + pinChange * clipped));
                    } else if (stateChanged) {
                        isAtMax =
                            !reset &&
                            clipped > prevProgress &&
                            end + 1 > scroll &&
                            scroll + 1 >= _maxScroll(scroller, direction); // if it's at the VERY end of the page, don't switch away from position: fixed because it's pointless and it could cause a brief flash when the user scrolls back up (when it gets pinned again)

                        if (pinReparent) {
                            if (!reset && (isActive || isAtMax)) {
                                var bounds = _getBounds(pin, true),
                                    _offset = scroll - start;

                                _reparent(
                                    pin,
                                    _body,
                                    bounds.top + (direction === _vertical ? _offset : 0) + _px,
                                    bounds.left + (direction === _vertical ? 0 : _offset) + _px
                                );
                            } else {
                                _reparent(pin, spacer);
                            }
                        }

                        _setState(isActive || isAtMax ? pinActiveState : pinState);

                        (pinMoves && clipped < 1 && isActive) ||
                            pinSetter(pinStart + (clipped === 1 && !isAtMax ? pinChange : 0));
                    }
                }

                snap && !tweenTo.tween && !_refreshing && !_startup && snapDelayedCall.restart(true);
                toggleClass &&
                    (toggled || (once && clipped && (clipped < 1 || !_limitCallbacks))) &&
                    _toArray(toggleClass.targets).forEach(function (el) {
                        return el.classList[isActive || once ? 'add' : 'remove'](toggleClass.className);
                    }); // classes could affect positioning, so do it even if reset or refreshing is true.

                onUpdate && !isToggle && !reset && onUpdate(self);

                if (stateChanged && !_refreshing) {
                    if (isToggle) {
                        if (isTakingAction) {
                            if (action === 'complete') {
                                animation.pause().totalProgress(1);
                            } else if (action === 'reset') {
                                animation.restart(true).pause();
                            } else if (action === 'restart') {
                                animation.restart(true);
                            } else {
                                animation[action]();
                            }
                        }

                        onUpdate && onUpdate(self);
                    }

                    if (toggled || !_limitCallbacks) {
                        // on startup, the page could be scrolled and we don't want to fire callbacks that didn't toggle. For example onEnter shouldn't fire if the ScrollTrigger isn't actually entered.
                        onToggle && toggled && _callback(self, onToggle);
                        callbacks[toggleState] && _callback(self, callbacks[toggleState]);
                        once && (clipped === 1 ? self.kill(false, 1) : (callbacks[toggleState] = 0)); // a callback shouldn't be called again if once is true.

                        if (!toggled) {
                            // it's possible to go completely past, like from before the start to after the end (or vice-versa) in which case BOTH callbacks should be fired in that order
                            toggleState = clipped === 1 ? 1 : 3;
                            callbacks[toggleState] && _callback(self, callbacks[toggleState]);
                        }
                    }

                    if (
                        fastScrollEnd &&
                        !isActive &&
                        Math.abs(self.getVelocity()) > (_isNumber(fastScrollEnd) ? fastScrollEnd : 2500)
                    ) {
                        _endAnimation(self.callbackAnimation);

                        scrubTween
                            ? scrubTween.progress(1)
                            : _endAnimation(animation, action === 'reverse' ? 1 : !clipped, 1);
                    }
                } else if (isToggle && onUpdate && !_refreshing) {
                    onUpdate(self);
                }
            } // update absolutely-positioned markers (only if the scroller isn't the viewport)

            if (markerEndSetter) {
                var n = containerAnimation
                    ? (scroll / containerAnimation.duration()) * (containerAnimation._caScrollDist || 0)
                    : scroll;
                markerStartSetter(n + (markerStartTrigger._isFlipped ? 1 : 0));
                markerEndSetter(n);
            }

            caMarkerSetter &&
                caMarkerSetter((-scroll / containerAnimation.duration()) * (containerAnimation._caScrollDist || 0));
        };

        self.enable = function (reset, refresh) {
            if (!self.enabled) {
                self.enabled = true;

                _addListener(scroller, 'resize', _onResize);

                isViewport || _addListener(scroller, 'scroll', _onScroll);
                onRefreshInit && _addListener(ScrollTrigger, 'refreshInit', onRefreshInit);

                if (reset !== false) {
                    self.progress = prevProgress = 0;
                    scroll1 = scroll2 = lastSnap = scrollFunc();
                }

                refresh !== false && self.refresh();
            }
        };

        self.getTween = function (snap) {
            return snap && tweenTo ? tweenTo.tween : scrubTween;
        };

        self.setPositions = function (newStart, newEnd, keepClamp, pinOffset) {
            // doesn't persist after refresh()! Intended to be a way to override values that were set during refresh(), like you could set it in onRefresh()
            if (containerAnimation) {
                // convert ratios into scroll positions. Remember, start/end values on ScrollTriggers that have a containerAnimation refer to the time (in seconds), NOT scroll positions.
                var st = containerAnimation.scrollTrigger,
                    duration = containerAnimation.duration(),
                    _change = st.end - st.start;

                newStart = st.start + (_change * newStart) / duration;
                newEnd = st.start + (_change * newEnd) / duration;
            }

            self.refresh(
                false,
                false,
                {
                    start: _keepClamp(newStart, keepClamp && !!self._startClamp),
                    end: _keepClamp(newEnd, keepClamp && !!self._endClamp)
                },
                pinOffset
            );
            self.update();
        };

        self.adjustPinSpacing = function (amount) {
            if (spacerState && amount) {
                var i = spacerState.indexOf(direction.d) + 1;
                spacerState[i] = parseFloat(spacerState[i]) + amount + _px;
                spacerState[1] = parseFloat(spacerState[1]) + amount + _px;

                _setState(spacerState);
            }
        };

        self.disable = function (reset, allowAnimation) {
            if (self.enabled) {
                reset !== false && self.revert(true, true);
                self.enabled = self.isActive = false;
                allowAnimation || (scrubTween && scrubTween.pause());
                prevScroll = 0;
                pinCache && (pinCache.uncache = 1);
                onRefreshInit && _removeListener(ScrollTrigger, 'refreshInit', onRefreshInit);

                if (snapDelayedCall) {
                    snapDelayedCall.pause();
                    tweenTo.tween && tweenTo.tween.kill() && (tweenTo.tween = 0);
                }

                if (!isViewport) {
                    var i = _triggers.length;

                    while (i--) {
                        if (_triggers[i].scroller === scroller && _triggers[i] !== self) {
                            return; //don't remove the listeners if there are still other triggers referencing it.
                        }
                    }

                    _removeListener(scroller, 'resize', _onResize);

                    isViewport || _removeListener(scroller, 'scroll', _onScroll);
                }
            }
        };

        self.kill = function (revert, allowAnimation) {
            self.disable(revert, allowAnimation);
            scrubTween && !allowAnimation && scrubTween.kill();
            id && delete _ids[id];

            var i = _triggers.indexOf(self);

            i >= 0 && _triggers.splice(i, 1);
            i === _i && _direction > 0 && _i--; // if we're in the middle of a refresh() or update(), splicing would cause skips in the index, so adjust...
            // if no other ScrollTrigger instances of the same scroller are found, wipe out any recorded scroll position. Otherwise, in a single page application, for example, it could maintain scroll position when it really shouldn't.

            i = 0;

            _triggers.forEach(function (t) {
                return t.scroller === self.scroller && (i = 1);
            });

            i || _refreshingAll || (self.scroll.rec = 0);

            if (animation) {
                animation.scrollTrigger = null;
                revert &&
                    animation.revert({
                        kill: false
                    });
                allowAnimation || animation.kill();
            }

            markerStart &&
                [markerStart, markerEnd, markerStartTrigger, markerEndTrigger].forEach(function (m) {
                    return m.parentNode && m.parentNode.removeChild(m);
                });
            _primary === self && (_primary = 0);

            if (pin) {
                pinCache && (pinCache.uncache = 1);
                i = 0;

                _triggers.forEach(function (t) {
                    return t.pin === pin && i++;
                });

                i || (pinCache.spacer = 0); // if there aren't any more ScrollTriggers with the same pin, remove the spacer, otherwise it could be contaminated with old/stale values if the user re-creates a ScrollTrigger for the same element.
            }

            vars.onKill && vars.onKill(self);
        };

        _triggers.push(self);

        self.enable(false, false);
        customRevertReturn && customRevertReturn(self);

        if (animation && animation.add && !change) {
            // if the animation is a timeline, it may not have been populated yet, so it wouldn't render at the proper place on the first refresh(), thus we should schedule one for the next tick. If "change" is defined, we know it must be re-enabling, thus we can refresh() right away.
            var updateFunc = self.update; // some browsers may fire a scroll event BEFORE a tick elapses and/or the DOMContentLoaded fires. So there's a chance update() will be called BEFORE a refresh() has happened on a Timeline-attached ScrollTrigger which means the start/end won't be calculated yet. We don't want to add conditional logic inside the update() method (like check to see if end is defined and if not, force a refresh()) because that's a function that gets hit a LOT (performance). So we swap out the real update() method for this one that'll re-attach it the first time it gets called and of course forces a refresh().

            self.update = function () {
                self.update = updateFunc;
                start || end || self.refresh();
            };

            gsap.delayedCall(0.01, self.update);
            change = 0.01;
            start = end = 0;
        } else {
            self.refresh();
        }

        pin && _queueRefreshAll(); // pinning could affect the positions of other things, so make sure we queue a full refresh()
    };

    ScrollTrigger.register = function register(core) {
        if (!_coreInitted) {
            gsap = core || _getGSAP();
            _windowExists() && window.document && ScrollTrigger.enable();
            _coreInitted = _enabled;
        }

        return _coreInitted;
    };

    ScrollTrigger.defaults = function defaults(config) {
        if (config) {
            for (var p in config) {
                _defaults[p] = config[p];
            }
        }

        return _defaults;
    };

    ScrollTrigger.disable = function disable(reset, kill) {
        _enabled = 0;

        _triggers.forEach(function (trigger) {
            return trigger[kill ? 'kill' : 'disable'](reset);
        });

        _removeListener(_win, 'wheel', _onScroll);

        _removeListener(_doc, 'scroll', _onScroll);

        clearInterval(_syncInterval);

        _removeListener(_doc, 'touchcancel', _passThrough);

        _removeListener(_body, 'touchstart', _passThrough);

        _multiListener(_removeListener, _doc, 'pointerdown,touchstart,mousedown', _pointerDownHandler);

        _multiListener(_removeListener, _doc, 'pointerup,touchend,mouseup', _pointerUpHandler);

        _resizeDelay.kill();

        _iterateAutoRefresh(_removeListener);

        for (var i = 0; i < _scrollers.length; i += 3) {
            _wheelListener(_removeListener, _scrollers[i], _scrollers[i + 1]);

            _wheelListener(_removeListener, _scrollers[i], _scrollers[i + 2]);
        }
    };

    ScrollTrigger.enable = function enable() {
        _win = window;
        _doc = document;
        _docEl = _doc.documentElement;
        _body = _doc.body;

        if (gsap) {
            _toArray = gsap.utils.toArray;
            _clamp = gsap.utils.clamp;
            _context = gsap.core.context || _passThrough;
            _suppressOverwrites = gsap.core.suppressOverwrites || _passThrough;
            _scrollRestoration = _win.history.scrollRestoration || 'auto';
            _lastScroll = _win.pageYOffset;
            gsap.core.globals('ScrollTrigger', ScrollTrigger); // must register the global manually because in Internet Explorer, functions (classes) don't have a "name" property.

            if (_body) {
                _enabled = 1;
                _div100vh = document.createElement('div'); // to solve mobile browser address bar show/hide resizing, we shouldn't rely on window.innerHeight. Instead, use a <div> with its height set to 100vh and measure that since that's what the scrolling is based on anyway and it's not affected by address bar showing/hiding.

                _div100vh.style.height = '100vh';
                _div100vh.style.position = 'absolute';

                _refresh100vh();

                _rafBugFix();

                Observer.register(gsap); // isTouch is 0 if no touch, 1 if ONLY touch, and 2 if it can accommodate touch but also other types like mouse/pointer.

                ScrollTrigger.isTouch = Observer.isTouch;
                _fixIOSBug = Observer.isTouch && /(iPad|iPhone|iPod|Mac)/g.test(navigator.userAgent); // since 2017, iOS has had a bug that causes event.clientX/Y to be inaccurate when a scroll occurs, thus we must alternate ignoring every other touchmove event to work around it. See https://bugs.webkit.org/show_bug.cgi?id=181954 and https://codepen.io/GreenSock/pen/ExbrPNa/087cef197dc35445a0951e8935c41503

                _ignoreMobileResize = Observer.isTouch === 1;

                _addListener(_win, 'wheel', _onScroll); // mostly for 3rd party smooth scrolling libraries.

                _root = [_win, _doc, _docEl, _body];

                if (gsap.matchMedia) {
                    ScrollTrigger.matchMedia = function (vars) {
                        var mm = gsap.matchMedia(),
                            p;

                        for (p in vars) {
                            mm.add(p, vars[p]);
                        }

                        return mm;
                    };

                    gsap.addEventListener('matchMediaInit', function () {
                        return _revertAll();
                    });
                    gsap.addEventListener('matchMediaRevert', function () {
                        return _revertRecorded();
                    });
                    gsap.addEventListener('matchMedia', function () {
                        _refreshAll(0, 1);

                        _dispatch('matchMedia');
                    });
                    gsap.matchMedia('(orientation: portrait)', function () {
                        // when orientation changes, we should take new base measurements for the ignoreMobileResize feature.
                        _setBaseDimensions();

                        return _setBaseDimensions;
                    });
                } else {
                    console.warn('Requires GSAP 3.11.0 or later');
                }

                _setBaseDimensions();

                _addListener(_doc, 'scroll', _onScroll); // some browsers (like Chrome), the window stops dispatching scroll events on the window if you scroll really fast, but it's consistent on the document!

                var bodyStyle = _body.style,
                    border = bodyStyle.borderTopStyle,
                    AnimationProto = gsap.core.Animation.prototype,
                    bounds,
                    i;
                AnimationProto.revert ||
                    Object.defineProperty(AnimationProto, 'revert', {
                        value: function value() {
                            return this.time(-0.01, true);
                        }
                    }); // only for backwards compatibility (Animation.revert() was added after 3.10.4)

                bodyStyle.borderTopStyle = 'solid'; // works around an issue where a margin of a child element could throw off the bounds of the _body, making it seem like there's a margin when there actually isn't. The border ensures that the bounds are accurate.

                bounds = _getBounds(_body);
                _vertical.m = Math.round(bounds.top + _vertical.sc()) || 0; // accommodate the offset of the <body> caused by margins and/or padding

                _horizontal.m = Math.round(bounds.left + _horizontal.sc()) || 0;
                border ? (bodyStyle.borderTopStyle = border) : bodyStyle.removeProperty('border-top-style'); // TODO: (?) maybe move to leveraging the velocity mechanism in Observer and skip intervals.

                _syncInterval = setInterval(_sync, 250);
                gsap.delayedCall(0.5, function () {
                    return (_startup = 0);
                });

                _addListener(_doc, 'touchcancel', _passThrough); // some older Android devices intermittently stop dispatching "touchmove" events if we don't listen for "touchcancel" on the document.

                _addListener(_body, 'touchstart', _passThrough); //works around Safari bug: https://gsap.com/forums/topic/21450-draggable-in-iframe-on-mobile-is-buggy/

                _multiListener(_addListener, _doc, 'pointerdown,touchstart,mousedown', _pointerDownHandler);

                _multiListener(_addListener, _doc, 'pointerup,touchend,mouseup', _pointerUpHandler);

                _transformProp = gsap.utils.checkPrefix('transform');

                _stateProps.push(_transformProp);

                _coreInitted = _getTime();
                _resizeDelay = gsap.delayedCall(0.2, _refreshAll).pause();
                _autoRefresh = [
                    _doc,
                    'visibilitychange',
                    function () {
                        var w = _win.innerWidth,
                            h = _win.innerHeight;

                        if (_doc.hidden) {
                            _prevWidth = w;
                            _prevHeight = h;
                        } else if (_prevWidth !== w || _prevHeight !== h) {
                            _onResize();
                        }
                    },
                    _doc,
                    'DOMContentLoaded',
                    _refreshAll,
                    _win,
                    'load',
                    _refreshAll,
                    _win,
                    'resize',
                    _onResize
                ];

                _iterateAutoRefresh(_addListener);

                _triggers.forEach(function (trigger) {
                    return trigger.enable(0, 1);
                });

                for (i = 0; i < _scrollers.length; i += 3) {
                    _wheelListener(_removeListener, _scrollers[i], _scrollers[i + 1]);

                    _wheelListener(_removeListener, _scrollers[i], _scrollers[i + 2]);
                }
            }
        }
    };

    ScrollTrigger.config = function config(vars) {
        'limitCallbacks' in vars && (_limitCallbacks = !!vars.limitCallbacks);
        var ms = vars.syncInterval;
        (ms && clearInterval(_syncInterval)) || ((_syncInterval = ms) && setInterval(_sync, ms));
        'ignoreMobileResize' in vars && (_ignoreMobileResize = ScrollTrigger.isTouch === 1 && vars.ignoreMobileResize);

        if ('autoRefreshEvents' in vars) {
            _iterateAutoRefresh(_removeListener) || _iterateAutoRefresh(_addListener, vars.autoRefreshEvents || 'none');
            _ignoreResize = (vars.autoRefreshEvents + '').indexOf('resize') === -1;
        }
    };

    ScrollTrigger.scrollerProxy = function scrollerProxy(target, vars) {
        var t = _getTarget(target),
            i = _scrollers.indexOf(t),
            isViewport = _isViewport(t);

        if (~i) {
            _scrollers.splice(i, isViewport ? 6 : 2);
        }

        if (vars) {
            isViewport ? _proxies.unshift(_win, vars, _body, vars, _docEl, vars) : _proxies.unshift(t, vars);
        }
    };

    ScrollTrigger.clearMatchMedia = function clearMatchMedia(query) {
        _triggers.forEach(function (t) {
            return t._ctx && t._ctx.query === query && t._ctx.kill(true, true);
        });
    };

    ScrollTrigger.isInViewport = function isInViewport(element, ratio, horizontal) {
        var bounds = (_isString(element) ? _getTarget(element) : element).getBoundingClientRect(),
            offset = bounds[horizontal ? _width : _height] * ratio || 0;
        return horizontal
            ? bounds.right - offset > 0 && bounds.left + offset < _win.innerWidth
            : bounds.bottom - offset > 0 && bounds.top + offset < _win.innerHeight;
    };

    ScrollTrigger.positionInViewport = function positionInViewport(element, referencePoint, horizontal) {
        _isString(element) && (element = _getTarget(element));
        var bounds = element.getBoundingClientRect(),
            size = bounds[horizontal ? _width : _height],
            offset =
                referencePoint == null
                    ? size / 2
                    : referencePoint in _keywords
                    ? _keywords[referencePoint] * size
                    : ~referencePoint.indexOf('%')
                    ? (parseFloat(referencePoint) * size) / 100
                    : parseFloat(referencePoint) || 0;
        return horizontal ? (bounds.left + offset) / _win.innerWidth : (bounds.top + offset) / _win.innerHeight;
    };

    ScrollTrigger.killAll = function killAll(allowListeners) {
        _triggers.slice(0).forEach(function (t) {
            return t.vars.id !== 'ScrollSmoother' && t.kill();
        });

        if (allowListeners !== true) {
            var listeners = _listeners.killAll || [];
            _listeners = {};
            listeners.forEach(function (f) {
                return f();
            });
        }
    };

    return ScrollTrigger;
})();
ScrollTrigger.version = '3.12.5';

ScrollTrigger.saveStyles = function (targets) {
    return targets
        ? _toArray(targets).forEach(function (target) {
              // saved styles are recorded in a consecutive alternating Array, like [element, cssText, transform attribute, cache, matchMedia, ...]
              if (target && target.style) {
                  var i = _savedStyles.indexOf(target);

                  i >= 0 && _savedStyles.splice(i, 5);

                  _savedStyles.push(
                      target,
                      target.style.cssText,
                      target.getBBox && target.getAttribute('transform'),
                      gsap.core.getCache(target),
                      _context()
                  );
              }
          })
        : _savedStyles;
};

ScrollTrigger.revert = function (soft, media) {
    return _revertAll(!soft, media);
};

ScrollTrigger.create = function (vars, animation) {
    return new ScrollTrigger(vars, animation);
};

ScrollTrigger.refresh = function (safe) {
    return safe ? _onResize() : (_coreInitted || ScrollTrigger.register()) && _refreshAll(true);
};

ScrollTrigger.update = function (force) {
    return ++_scrollers.cache && _updateAll(force === true ? 2 : 0);
};

ScrollTrigger.clearScrollMemory = _clearScrollMemory;

ScrollTrigger.maxScroll = function (element, horizontal) {
    return _maxScroll(element, horizontal ? _horizontal : _vertical);
};

ScrollTrigger.getScrollFunc = function (element, horizontal) {
    return _getScrollFunc(_getTarget(element), horizontal ? _horizontal : _vertical);
};

ScrollTrigger.getById = function (id) {
    return _ids[id];
};

ScrollTrigger.getAll = function () {
    return _triggers.filter(function (t) {
        return t.vars.id !== 'ScrollSmoother';
    });
}; // it's common for people to ScrollTrigger.getAll(t => t.kill()) on page routes, for example, and we don't want it to ruin smooth scrolling by killing the main ScrollSmoother one.

ScrollTrigger.isScrolling = function () {
    return !!_lastScrollTime;
};

ScrollTrigger.snapDirectional = _snapDirectional;

ScrollTrigger.addEventListener = function (type, callback) {
    var a = _listeners[type] || (_listeners[type] = []);
    ~a.indexOf(callback) || a.push(callback);
};

ScrollTrigger.removeEventListener = function (type, callback) {
    var a = _listeners[type],
        i = a && a.indexOf(callback);
    i >= 0 && a.splice(i, 1);
};

ScrollTrigger.batch = function (targets, vars) {
    var result = [],
        varsCopy = {},
        interval = vars.interval || 0.016,
        batchMax = vars.batchMax || 1e9,
        proxyCallback = function proxyCallback(type, callback) {
            var elements = [],
                triggers = [],
                delay = gsap
                    .delayedCall(interval, function () {
                        callback(elements, triggers);
                        elements = [];
                        triggers = [];
                    })
                    .pause();
            return function (self) {
                elements.length || delay.restart(true);
                elements.push(self.trigger);
                triggers.push(self);
                batchMax <= elements.length && delay.progress(1);
            };
        },
        p;

    for (p in vars) {
        varsCopy[p] =
            p.substr(0, 2) === 'on' && _isFunction(vars[p]) && p !== 'onRefreshInit'
                ? proxyCallback(p, vars[p])
                : vars[p];
    }

    if (_isFunction(batchMax)) {
        batchMax = batchMax();

        _addListener(ScrollTrigger, 'refresh', function () {
            return (batchMax = vars.batchMax());
        });
    }

    _toArray(targets).forEach(function (target) {
        var config = {};

        for (p in varsCopy) {
            config[p] = varsCopy[p];
        }

        config.trigger = target;
        result.push(ScrollTrigger.create(config));
    });

    return result;
}; // to reduce file size. clamps the scroll and also returns a duration multiplier so that if the scroll gets chopped shorter, the duration gets curtailed as well (otherwise if you're very close to the top of the page, for example, and swipe up really fast, it'll suddenly slow down and take a long time to reach the top).

var _clampScrollAndGetDurationMultiplier = function _clampScrollAndGetDurationMultiplier(
        scrollFunc,
        current,
        end,
        max
    ) {
        current > max ? scrollFunc(max) : current < 0 && scrollFunc(0);
        return end > max ? (max - current) / (end - current) : end < 0 ? current / (current - end) : 1;
    },
    _allowNativePanning = function _allowNativePanning(target, direction) {
        if (direction === true) {
            target.style.removeProperty('touch-action');
        } else {
            target.style.touchAction =
                direction === true
                    ? 'auto'
                    : direction
                    ? 'pan-' + direction + (Observer.isTouch ? ' pinch-zoom' : '')
                    : 'none'; // note: Firefox doesn't support it pinch-zoom properly, at least in addition to a pan-x or pan-y.
        }

        target === _docEl && _allowNativePanning(_body, direction);
    },
    _overflow = {
        auto: 1,
        scroll: 1
    },
    _nestedScroll = function _nestedScroll(_ref5) {
        var event = _ref5.event,
            target = _ref5.target,
            axis = _ref5.axis;

        var node = (event.changedTouches ? event.changedTouches[0] : event).target,
            cache = node._gsap || gsap.core.getCache(node),
            time = _getTime(),
            cs;

        if (!cache._isScrollT || time - cache._isScrollT > 2000) {
            // cache for 2 seconds to improve performance.
            while (
                node &&
                node !== _body &&
                ((node.scrollHeight <= node.clientHeight && node.scrollWidth <= node.clientWidth) ||
                    !(_overflow[(cs = _getComputedStyle(node)).overflowY] || _overflow[cs.overflowX]))
            ) {
                node = node.parentNode;
            }

            cache._isScroll =
                node &&
                node !== target &&
                !_isViewport(node) &&
                (_overflow[(cs = _getComputedStyle(node)).overflowY] || _overflow[cs.overflowX]);
            cache._isScrollT = time;
        }

        if (cache._isScroll || axis === 'x') {
            event.stopPropagation();
            event._gsapAllow = true;
        }
    },
    // capture events on scrollable elements INSIDE the <body> and allow those by calling stopPropagation() when we find a scrollable ancestor
    _inputObserver = function _inputObserver(target, type, inputs, nested) {
        return Observer.create({
            target: target,
            capture: true,
            debounce: false,
            lockAxis: true,
            type: type,
            onWheel: (nested = nested && _nestedScroll),
            onPress: nested,
            onDrag: nested,
            onScroll: nested,
            onEnable: function onEnable() {
                return inputs && _addListener(_doc, Observer.eventTypes[0], _captureInputs, false, true);
            },
            onDisable: function onDisable() {
                return _removeListener(_doc, Observer.eventTypes[0], _captureInputs, true);
            }
        });
    },
    _inputExp = /(input|label|select|textarea)/i,
    _inputIsFocused,
    _captureInputs = function _captureInputs(e) {
        var isInput = _inputExp.test(e.target.tagName);

        if (isInput || _inputIsFocused) {
            e._gsapAllow = true;
            _inputIsFocused = isInput;
        }
    },
    _getScrollNormalizer = function _getScrollNormalizer(vars) {
        _isObject(vars) || (vars = {});
        vars.preventDefault = vars.isNormalizer = vars.allowClicks = true;
        vars.type || (vars.type = 'wheel,touch');
        vars.debounce = !!vars.debounce;
        vars.id = vars.id || 'normalizer';

        var _vars2 = vars,
            normalizeScrollX = _vars2.normalizeScrollX,
            momentum = _vars2.momentum,
            allowNestedScroll = _vars2.allowNestedScroll,
            onRelease = _vars2.onRelease,
            self,
            maxY,
            target = _getTarget(vars.target) || _docEl,
            smoother = gsap.core.globals().ScrollSmoother,
            smootherInstance = smoother && smoother.get(),
            content =
                _fixIOSBug &&
                ((vars.content && _getTarget(vars.content)) ||
                    (smootherInstance &&
                        vars.content !== false &&
                        !smootherInstance.smooth() &&
                        smootherInstance.content())),
            scrollFuncY = _getScrollFunc(target, _vertical),
            scrollFuncX = _getScrollFunc(target, _horizontal),
            scale = 1,
            initialScale =
                (Observer.isTouch && _win.visualViewport
                    ? _win.visualViewport.scale * _win.visualViewport.width
                    : _win.outerWidth) / _win.innerWidth,
            wheelRefresh = 0,
            resolveMomentumDuration = _isFunction(momentum)
                ? function () {
                      return momentum(self);
                  }
                : function () {
                      return momentum || 2.8;
                  },
            lastRefreshID,
            skipTouchMove,
            inputObserver = _inputObserver(target, vars.type, true, allowNestedScroll),
            resumeTouchMove = function resumeTouchMove() {
                return (skipTouchMove = false);
            },
            scrollClampX = _passThrough,
            scrollClampY = _passThrough,
            updateClamps = function updateClamps() {
                maxY = _maxScroll(target, _vertical);
                scrollClampY = _clamp(_fixIOSBug ? 1 : 0, maxY);
                normalizeScrollX && (scrollClampX = _clamp(0, _maxScroll(target, _horizontal)));
                lastRefreshID = _refreshID;
            },
            removeContentOffset = function removeContentOffset() {
                content._gsap.y = _round(parseFloat(content._gsap.y) + scrollFuncY.offset) + 'px';
                content.style.transform =
                    'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, ' + parseFloat(content._gsap.y) + ', 0, 1)';
                scrollFuncY.offset = scrollFuncY.cacheID = 0;
            },
            ignoreDrag = function ignoreDrag() {
                if (skipTouchMove) {
                    requestAnimationFrame(resumeTouchMove);

                    var offset = _round(self.deltaY / 2),
                        scroll = scrollClampY(scrollFuncY.v - offset);

                    if (content && scroll !== scrollFuncY.v + scrollFuncY.offset) {
                        scrollFuncY.offset = scroll - scrollFuncY.v;

                        var y = _round((parseFloat(content && content._gsap.y) || 0) - scrollFuncY.offset);

                        content.style.transform = 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, ' + y + ', 0, 1)';
                        content._gsap.y = y + 'px';
                        scrollFuncY.cacheID = _scrollers.cache;

                        _updateAll();
                    }

                    return true;
                }

                scrollFuncY.offset && removeContentOffset();
                skipTouchMove = true;
            },
            tween,
            startScrollX,
            startScrollY,
            onStopDelayedCall,
            onResize = function onResize() {
                // if the window resizes, like on an iPhone which Apple FORCES the address bar to show/hide even if we event.preventDefault(), it may be scrolling too far now that the address bar is showing, so we must dynamically adjust the momentum tween.
                updateClamps();

                if (tween.isActive() && tween.vars.scrollY > maxY) {
                    scrollFuncY() > maxY ? tween.progress(1) && scrollFuncY(maxY) : tween.resetTo('scrollY', maxY);
                }
            };

        content &&
            gsap.set(content, {
                y: '+=0'
            }); // to ensure there's a cache (element._gsap)

        vars.ignoreCheck = function (e) {
            return (
                (_fixIOSBug && e.type === 'touchmove' && ignoreDrag(e)) ||
                (scale > 1.05 && e.type !== 'touchstart') ||
                self.isGesturing ||
                (e.touches && e.touches.length > 1)
            );
        };

        vars.onPress = function () {
            skipTouchMove = false;
            var prevScale = scale;
            scale = _round(((_win.visualViewport && _win.visualViewport.scale) || 1) / initialScale);
            tween.pause();
            prevScale !== scale && _allowNativePanning(target, scale > 1.01 ? true : normalizeScrollX ? false : 'x');
            startScrollX = scrollFuncX();
            startScrollY = scrollFuncY();
            updateClamps();
            lastRefreshID = _refreshID;
        };

        vars.onRelease = vars.onGestureStart = function (self, wasDragging) {
            scrollFuncY.offset && removeContentOffset();

            if (!wasDragging) {
                onStopDelayedCall.restart(true);
            } else {
                _scrollers.cache++; // make sure we're pulling the non-cached value
                // alternate algorithm: durX = Math.min(6, Math.abs(self.velocityX / 800)),	dur = Math.max(durX, Math.min(6, Math.abs(self.velocityY / 800))); dur = dur * (0.4 + (1 - _power4In(dur / 6)) * 0.6)) * (momentumSpeed || 1)

                var dur = resolveMomentumDuration(),
                    currentScroll,
                    endScroll;

                if (normalizeScrollX) {
                    currentScroll = scrollFuncX();
                    endScroll = currentScroll + (dur * 0.05 * -self.velocityX) / 0.227; // the constant .227 is from power4(0.05). velocity is inverted because scrolling goes in the opposite direction.

                    dur *= _clampScrollAndGetDurationMultiplier(
                        scrollFuncX,
                        currentScroll,
                        endScroll,
                        _maxScroll(target, _horizontal)
                    );
                    tween.vars.scrollX = scrollClampX(endScroll);
                }

                currentScroll = scrollFuncY();
                endScroll = currentScroll + (dur * 0.05 * -self.velocityY) / 0.227; // the constant .227 is from power4(0.05)

                dur *= _clampScrollAndGetDurationMultiplier(
                    scrollFuncY,
                    currentScroll,
                    endScroll,
                    _maxScroll(target, _vertical)
                );
                tween.vars.scrollY = scrollClampY(endScroll);
                tween.invalidate().duration(dur).play(0.01);

                if ((_fixIOSBug && tween.vars.scrollY >= maxY) || currentScroll >= maxY - 1) {
                    // iOS bug: it'll show the address bar but NOT fire the window "resize" event until the animation is done but we must protect against overshoot so we leverage an onUpdate to do so.
                    gsap.to(
                        {},
                        {
                            onUpdate: onResize,
                            duration: dur
                        }
                    );
                }
            }

            onRelease && onRelease(self);
        };

        vars.onWheel = function () {
            tween._ts && tween.pause();

            if (_getTime() - wheelRefresh > 1000) {
                // after 1 second, refresh the clamps otherwise that'll only happen when ScrollTrigger.refresh() is called or for touch-scrolling.
                lastRefreshID = 0;
                wheelRefresh = _getTime();
            }
        };

        vars.onChange = function (self, dx, dy, xArray, yArray) {
            _refreshID !== lastRefreshID && updateClamps();
            dx &&
                normalizeScrollX &&
                scrollFuncX(
                    scrollClampX(
                        xArray[2] === dx ? startScrollX + (self.startX - self.x) : scrollFuncX() + dx - xArray[1]
                    )
                ); // for more precision, we track pointer/touch movement from the start, otherwise it'll drift.

            if (dy) {
                scrollFuncY.offset && removeContentOffset();
                var isTouch = yArray[2] === dy,
                    y = isTouch ? startScrollY + self.startY - self.y : scrollFuncY() + dy - yArray[1],
                    yClamped = scrollClampY(y);
                isTouch && y !== yClamped && (startScrollY += yClamped - y);
                scrollFuncY(yClamped);
            }

            (dy || dx) && _updateAll();
        };

        vars.onEnable = function () {
            _allowNativePanning(target, normalizeScrollX ? false : 'x');

            ScrollTrigger.addEventListener('refresh', onResize);

            _addListener(_win, 'resize', onResize);

            if (scrollFuncY.smooth) {
                scrollFuncY.target.style.scrollBehavior = 'auto';
                scrollFuncY.smooth = scrollFuncX.smooth = false;
            }

            inputObserver.enable();
        };

        vars.onDisable = function () {
            _allowNativePanning(target, true);

            _removeListener(_win, 'resize', onResize);

            ScrollTrigger.removeEventListener('refresh', onResize);
            inputObserver.kill();
        };

        vars.lockAxis = vars.lockAxis !== false;
        self = new Observer(vars);
        self.iOS = _fixIOSBug; // used in the Observer getCachedScroll() function to work around an iOS bug that wreaks havoc with TouchEvent.clientY if we allow scroll to go all the way back to 0.

        _fixIOSBug && !scrollFuncY() && scrollFuncY(1); // iOS bug causes event.clientY values to freak out (wildly inaccurate) if the scroll position is exactly 0.

        _fixIOSBug && gsap.ticker.add(_passThrough); // prevent the ticker from sleeping

        onStopDelayedCall = self._dc;
        tween = gsap.to(self, {
            ease: 'power4',
            paused: true,
            inherit: false,
            scrollX: normalizeScrollX ? '+=0.1' : '+=0',
            scrollY: '+=0.1',
            modifiers: {
                scrollY: _interruptionTracker(scrollFuncY, scrollFuncY(), function () {
                    return tween.pause();
                })
            },
            onUpdate: _updateAll,
            onComplete: onStopDelayedCall.vars.onComplete
        }); // we need the modifier to sense if the scroll position is altered outside of the momentum tween (like with a scrollTo tween) so we can pause() it to prevent conflicts.

        return self;
    };

ScrollTrigger.sort = function (func) {
    return _triggers.sort(
        func ||
            function (a, b) {
                return (
                    (a.vars.refreshPriority || 0) * -1e6 + a.start - (b.start + (b.vars.refreshPriority || 0) * -1e6)
                );
            }
    );
};

ScrollTrigger.observe = function (vars) {
    return new Observer(vars);
};

ScrollTrigger.normalizeScroll = function (vars) {
    if (typeof vars === 'undefined') {
        return _normalizer;
    }

    if (vars === true && _normalizer) {
        return _normalizer.enable();
    }

    if (vars === false) {
        _normalizer && _normalizer.kill();
        _normalizer = vars;
        return;
    }

    var normalizer = vars instanceof Observer ? vars : _getScrollNormalizer(vars);
    _normalizer && _normalizer.target === normalizer.target && _normalizer.kill();
    _isViewport(normalizer.target) && (_normalizer = normalizer);
    return normalizer;
};

ScrollTrigger.core = {
    // smaller file size way to leverage in ScrollSmoother and Observer
    _getVelocityProp: _getVelocityProp,
    _inputObserver: _inputObserver,
    _scrollers: _scrollers,
    _proxies: _proxies,
    bridge: {
        // when normalizeScroll sets the scroll position (ss = setScroll)
        ss: function ss() {
            _lastScrollTime || _dispatch('scrollStart');
            _lastScrollTime = _getTime();
        },
        // a way to get the _refreshing value in Observer
        ref: function ref() {
            return _refreshing;
        }
    }
};
_getGSAP() && gsap.registerPlugin(ScrollTrigger);
export {ScrollTrigger as default};
